import { registerStyles } from "../utils/registerStyles";
import useVisibleProjects from "../hooks/useVisibleProjects";
import { MY_COMPANY_PROJECT_ID } from "../utils/projectVisibility";
import { employeeWorkTime, monthlyWorkTimeHours } from "../utils/workTimeMeasure";
import { scheduleHours } from "../utils/scheduleHours";
import { calculateShiftHours, calculateOvertimeForShift } from "../utils/timeReportHours";
import { resolvePayrollTime } from "../utils/payrollTime";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaBell,
  FaCalendarPlus,
  FaCalendarAlt,
  FaEllipsisV,
  FaChevronDown,
  FaClock,
  FaMagic,
  FaLock,
  FaPlus,
  FaTrash,
  FaTimes,
  FaUserSlash,
  FaUsers,
} from "react-icons/fa";
import { FaFilePen } from "react-icons/fa6";
import PopupShift from "../popup/PopupShift";
import useScheduleNotifications from "../hooks/useScheduleNotifications";
import NotificationsPopup from "../popup/NotificationsPopup";
import PublishPopup from "../popup/PublishPopup";
import ConnectedSchedulingPopup from "../popup/ConnectedSchedulingPopup";
import CreateAutomaticShiftPopup from "../popup/CreateAutomaticShiftPopup";
import LoadingIndicator from "../components/LoadingIndicator";
import {
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { mergeSchedulingEmployees, readSchedulingSchemas } from "../utils/schedulingPopupData";
 
// Global cache - finns kvar i minnet så länge sidan inte laddas om
const shiftPageCache = {
  showUnstaffedRow: true,
  schemas: null,
  schemasCompanyUid: null,
  schemasLoaded: false,
  schemasLoading: false,
  schemasUnsubscribe: null,
  shiftsCompanyUid: null,
  shiftsUnsubscribe: null,
  employees: null,
  employeesCompanyUid: null,
  employeesLoaded: false,
  employeesLoading: false,
  employeesUnsubscribe: null,
  projects: null,
  projectsCompanyUid: null,
  projectsLoaded: false,
  projectsLoading: false,
  projectsUnsubscribe: null,
  publishSettings: null,
  publishCompanyUid: null,
  publishLoaded: false,
  publishUnsubscribe: null,
  bookings: null,
  bookingsCompanyUid: null,
  bookingsLoaded: false,
  bookingsUnsubscribe: null,
  approvedOvertime: null,
  overtimeCompanyUid: null,
  overtimeLoaded: false,
  overtimeUnsubscribe: null,
  approvedAbsence: null,
  absenceCompanyUid: null,
  absenceLoaded: false,
  absenceUnsubscribe: null,
};
 
const COLORS = {
  blue: "#2563eb",
  blueLight: "#3b82f6",
  blueSoft: "#eff6ff",
  green: "#16c79a",
  red: "#ff5e5e",
  orange: "#ffaa5e",
  gray: "#8891a8",
  lightGray: "#f5f6fb",
  border: "#ececf4",
  yellowBg: "#fef3c7",
  yellowBorder: "#f59e0b",
  yellowText: "#92400e",
  dark: "#252b42",
};

function UnstaffedInfo() {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(290, window.innerWidth - 16);
      setPosition({
        left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
        top: rect.bottom + 96 < window.innerHeight ? rect.bottom + 8 : Math.max(8, rect.top - 104),
        width,
      });
    };
    const closeOutside = (event) => {
      if (!buttonRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    updatePosition();
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="shift-unstaffed-info-button"
        aria-label="Information om obemannade pass"
        aria-expanded={open}
        aria-describedby={open ? "shift-unstaffed-info" : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        i
      </button>
      {open && position && createPortal(
        <span id="shift-unstaffed-info" role="tooltip" className="shift-unstaffed-info-bubble" style={position}>
          När du skapar ett obemannat pass får alla användare en notis. Passet visas som ett ledigt pass och kan bokas i appen.
        </span>,
        document.body
      )}
    </>
  );
}

function PayrollLockInfo({ small = false }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(260, window.innerWidth - 16);
      setPosition({
        left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
        top: rect.bottom + 120 < window.innerHeight ? rect.bottom + 6 : Math.max(8, rect.top - 126),
        width,
      });
    };
    const closeOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    updatePosition();
    document.addEventListener("mousedown", closeOutside);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  return (
    <span ref={wrapperRef} className="shift-payroll-lock" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        ref={buttonRef}
        className="shift-payroll-lock-button"
        aria-label="Varför är passet låst?"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <FaLock size={small ? 10 : 12} aria-hidden="true" />
      </button>
      {open && position && createPortal(
        <span className="shift-payroll-lock-bubble" role="status" style={position}>
          Passet är låst eftersom personens löneunderlag är godkänt i Löneexport.
          Avmarkera Godkänd för personen där för att kunna ändra passet.
        </span>,
        document.body
      )}
    </span>
  );
}
 
const boxStyle = {
  position: "relative",
  display: "inline-block",
  flex: "0 1 220px",
  minWidth: 150,
  maxWidth: 260,
};
 
const inputStyle = {
  width: "100%",
  height: 50,
  padding: "8px 34px 8px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 3,
  background: "#ffffff",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
};
 
const labelStyle = {
  position: "absolute",
  top: -10,
  left: 12,
  fontSize: "0.7rem",
  background: "#ffffff",
  padding: "0 4px",
  color: "#6b7280",
  userSelect: "none",
};
 
const toolbarButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 42,
  height: 42,
  borderRadius: 8,
  color: "white",
  fontSize: 18,
  padding: 0,
  boxSizing: "border-box",
  cursor: "pointer",
};
 
function safeParseDate(dateValue) {
  if (!dateValue) return null;
 
  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : new Date(dateValue);
    }
 
    if (
      dateValue &&
      typeof dateValue === "object" &&
      typeof dateValue.toDate === "function"
    ) {
      const parsed = dateValue.toDate();
      return parsed instanceof Date && !isNaN(parsed.getTime()) ? new Date(parsed) : null;
    }
 
    if (typeof dateValue === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return !isNaN(date.getTime()) ? date : null;
      }
      
      const date = new Date(dateValue);
      return !isNaN(date.getTime()) ? date : null;
    }
 
    if (typeof dateValue === "number") {
      const date = new Date(dateValue);
      return !isNaN(date.getTime()) ? date : null;
    }
 
    return null;
  } catch (error) {
    console.error("Fel vid parsning av datum:", error);
    return null;
  }
}
 
function getWeekNumber(date) {
  if (!date) return 0;
 
  try {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  } catch (error) {
    console.error("Fel vid beräkning av veckonummer:", error);
    return 0;
  }
}
 
function getMonday(date) {
  if (!date) return new Date();
 
  try {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  } catch (error) {
    console.error("Fel vid beräkning av måndag:", error);
    return new Date();
  }
}
 
function getMonthDays(date) {
  if (!date) return [];
 
  try {
    const year = date.getFullYear();
    const month = date.getMonth();
 
    const firstDay = new Date(year, month, 1);
    const startDate = getMonday(firstDay);
 
    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
 
    return days;
  } catch (error) {
    console.error("Fel vid generering av månadsdagar:", error);
    return [];
  }
}
 
function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;
 
  try {
    const d1 = safeParseDate(date1);
    const d2 = safeParseDate(date2);
 
    if (!d1 || !d2) return false;
 
    return d1.toDateString() === d2.toDateString();
  } catch (error) {
    return false;
  }
}
 
function formatHoursText(hours) {
  return `${Number(hours || 0).toLocaleString("sv-SE", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} h`;
}
 
function formatOvertimeBadgeTime(overtimeItem) {
  if (overtimeItem.appliesToWholeShift) return "Hela passet";
 
  if (overtimeItem.startTime && overtimeItem.endTime) {
    return `${overtimeItem.startTime} - ${overtimeItem.endTime}`;
  }
 
  if (overtimeItem.hours) {
    return formatHoursText(overtimeItem.hours);
  }
 
  return "Tid saknas";
}
 
function formatAbsenceBadgeTime(absenceItem) {
  if (absenceItem.appliesToWholeShift) return "Hela passet";
 
  if (absenceItem.startTime && absenceItem.endTime) {
    return `${absenceItem.startTime} - ${absenceItem.endTime}`;
  }
 
  if (absenceItem.hours) {
    return formatHoursText(absenceItem.hours);
  }
 
  return "Tid saknas";
}
 
const getDetailedBadges = (employeeData) => {
  const badges = [];
 
  if (employeeData.breaks && employeeData.breaks.length > 0) {
    employeeData.breaks.forEach((breakItem) => {
      badges.push({
        type: "break",
        label: "Rast",
        time: breakItem.appliesToWholeShift
          ? "Hela passet"
          : `${breakItem.startTime || "?"} - ${breakItem.endTime || "?"}`,
        bgColor: "#dbeafe",
        color: "#1e40af",
        borderColor: "#bfdbfe",
      });
    });
  }
 
  if (employeeData.overtime && employeeData.overtime.length > 0) {
    employeeData.overtime.forEach((overtimeItem) => {
      badges.push({
        type: "overtime",
        label: overtimeItem.overtimeRuleName || "Övertid",
        time: formatOvertimeBadgeTime(overtimeItem),
        bgColor: overtimeItem.approvedFromRequest ? "#dcfce7" : "#fed7aa",
        color: overtimeItem.approvedFromRequest ? "#166534" : "#92400e",
        borderColor: overtimeItem.approvedFromRequest ? "#86efac" : "#fed7aa",
      });
    });
  }
 
  if (employeeData.absences && employeeData.absences.length > 0) {
    employeeData.absences.forEach((absenceItem) => {
      badges.push({
        type: "absence",
        label: absenceItem.absenceTypeName || "Frånvaro",
        time: formatAbsenceBadgeTime(absenceItem),
        bgColor: absenceItem.approvedFromRequest ? "#fee2e2" : "#fee2e2",
        color: absenceItem.approvedFromRequest ? "#991b1b" : "#991b1b",
        borderColor: absenceItem.approvedFromRequest ? "#fecaca" : "#fecaca",
      });
    });
  }
 
  return badges;
};
 
function getEmployeeIdFromRequest(request, employeeList = []) {
  if (!request) return null;
 
  if (request.employeeId) return request.employeeId;
  if (request.userId) return request.userId;
  if (request.uid) return request.uid;
 
  const email = String(request.userEmail || request.email || "")
    .trim()
    .toLowerCase();
 
  if (!email) return null;
 
  const employee = employeeList.find(
    (emp) => String(emp.email || "").trim().toLowerCase() === email
  );
 
  return employee?.id || null;
}
 
function buildApprovedOvertimeBadgeEntry(request) {
  return {
    overtimeRequestId: request.id,
    startTime: request.startTime || "",
    endTime: request.endTime || "",
    appliesToWholeShift: Boolean(request.appliesToWholeShift),
    overtimeRuleId: request.overtimeRuleId || "approved-request",
    overtimeRuleName: request.overtimeRuleName || "Godkänd övertid",
    percentage: Number(request.percentage || 0),
    hours: Number(request.hours || 0),
    approvedFromRequest: true,
  };
}
 
function buildApprovedAbsenceBadgeEntry(request) {
  return {
    absenceRequestId: request.id,
    startTime: request.startTime || "",
    endTime: request.endTime || "",
    appliesToWholeShift: Boolean(request.appliesToWholeShift),
    absenceTypeId: request.absenceTypeId || "approved-request",
    absenceTypeName: request.absenceTypeName || "Godkänd frånvaro",
    absencePercent: Number(request.absencePercent ?? request.salaryPercent ?? 0),
    hours: Number(request.hours || 0),
    approvedFromRequest: true,
  };
}
 
function mergeApprovedOvertimeIntoEmployeeData({
  shift,
  employeeData,
  employeeId,
  approvedOvertimeRequests,
  employees,
}) {
  const shiftId = shift?.originalId || shift?.id;
  if (!shiftId || !employeeId) return employeeData;
 
  const currentOvertime = Array.isArray(employeeData?.overtime)
    ? employeeData.overtime.filter((item) => !item?.approvedFromRequest)
    : [];
 
  const matchingRequests = (approvedOvertimeRequests || []).filter((request) => {
    const requestShiftId = request?.shiftId || request?.originalShiftId;
    if (requestShiftId !== shiftId) return false;
 
    const requestEmployeeId = getEmployeeIdFromRequest(request, employees);
    return requestEmployeeId === employeeId;
  });
 
  return {
    ...employeeData,
    overtime: [
      ...currentOvertime,
      ...matchingRequests.map(buildApprovedOvertimeBadgeEntry),
    ],
  };
}
 
function mergeApprovedAbsenceIntoEmployeeData({
  shift,
  employeeData,
  employeeId,
  approvedAbsenceRequests,
  employees,
}) {
  const shiftId = shift?.originalId || shift?.id;
  if (!shiftId || !employeeId) return employeeData;
 
  const currentAbsences = Array.isArray(employeeData?.absences)
    ? employeeData.absences.filter((item) => !item?.approvedFromRequest)
    : [];
 
  const matchingRequests = (approvedAbsenceRequests || []).filter((request) => {
    const requestShiftId = request?.shiftId || request?.originalShiftId;
    if (requestShiftId !== shiftId) return false;
 
    const requestEmployeeId = getEmployeeIdFromRequest(request, employees);
    return requestEmployeeId === employeeId;
  });
 
  return {
    ...employeeData,
    absences: [
      ...currentAbsences,
      ...matchingRequests.map(buildApprovedAbsenceBadgeEntry),
    ],
  };
}
 
function mergeApprovedRequestsIntoEmployeeData({
  shift,
  employeeData,
  employeeId,
  approvedOvertimeRequests,
  approvedAbsenceRequests,
  employees,
}) {
  const withOvertime = mergeApprovedOvertimeIntoEmployeeData({
    shift,
    employeeData,
    employeeId,
    approvedOvertimeRequests,
    employees,
  });
 
  return mergeApprovedAbsenceIntoEmployeeData({
    shift,
    employeeData: withOvertime,
    employeeId,
    approvedAbsenceRequests,
    employees,
  });
}
 
export default function ShiftPage() {
  const { currentUser } = useAuth();
  const { activeCompany, loading: companyLoading, allowedSchemaIds, permissionsLoading } = useCompany();
 
  const [selectedSchema, setSelectedSchema] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week");
  const [showDayColumns, setShowDayColumns] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [weekRowMode, setWeekRowMode] = useState("employees");
  const [showUnstaffedRow, setShowUnstaffedRow] = useState(
    () => shiftPageCache.showUnstaffedRow
  );
  useEffect(() => {
    shiftPageCache.showUnstaffedRow = showUnstaffedRow;
  }, [showUnstaffedRow]);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showPublishPopup, setShowPublishPopup] = useState(false);
  const [showSchedulingPopup, setShowSchedulingPopup] = useState(false);
  const [unassignedEmployeeCount, setUnassignedEmployeeCount] = useState(0);
  const [showSchemaRequiredPopup, setShowSchemaRequiredPopup] = useState(false);
  const [showNotificationsPopup, setShowNotificationsPopup] = useState(false);
  const [showSheetsPopup, setShowSheetsPopup] = useState(false);
  const [isQuickDeleteMode, setIsQuickDeleteMode] = useState(false);
  const [deletingShiftIds, setDeletingShiftIds] = useState(() => new Set());
  const [showUnstaffedPanel, setShowUnstaffedPanel] = useState(
    () => new URLSearchParams(window.location.search).get("panel") === "unstaffed"
  );
  const [selectedPopupDate, setSelectedPopupDate] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [popupPreselectedEmployee, setPopupPreselectedEmployee] = useState(null);
  const [popupPreselectedObemannad, setPopupPreselectedObemannad] = useState(false);
  const [popupPreselectedProject, setPopupPreselectedProject] = useState(null);
  const [publishedUntil, setPublishedUntil] = useState(null);
 
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempSelectedDate, setTempSelectedDate] = useState(new Date());
  const datePickerRef = useRef(null);
  const dateDisplayRef = useRef(null);
 
  const [showBookingsPopup, setShowBookingsPopup] = useState(false);
  const [selectedShiftForBookings, setSelectedShiftForBookings] = useState(null);
 
  const [schemas, setSchemas] = useState([]);
  const [schemasReadyCompanyUid, setSchemasReadyCompanyUid] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [employedEmployees, setEmployees] = useState([]);
  const [employeesReadyCompanyUid, setEmployeesReadyCompanyUid] = useState(null);
  const [scheduleLoadErrorCompanyUid, setScheduleLoadErrorCompanyUid] = useState(null);
  const [ownerEmployee, setOwnerEmployee] = useState(null);
  const [workTimeMeasures, setWorkTimeMeasures] = useState({ companyUid: null, byId: {} });
  useEffect(() => {
    const companyUid = activeCompany?.companyAccountUid;
    setWorkTimeMeasures({ companyUid, byId: {} });
    if (!companyUid) return;
    return onSnapshot(collection(db, "users", companyUid, "workTimeMeasures"), (snapshot) => {
      setWorkTimeMeasures({
        companyUid,
        byId: Object.fromEntries(snapshot.docs.map((entry) => [entry.id, entry.data()])),
      });
    }, (error) => {
      console.error("Kunde inte hämta arbetstidsmått:", error);
      setWorkTimeMeasures({ companyUid, byId: {} });
    });
  }, [activeCompany?.companyAccountUid]);
  const employees = useMemo(() => mergeSchedulingEmployees(employedEmployees, ownerEmployee),
    [employedEmployees, ownerEmployee]);
  const [allProjects, setProjects] = useState([]);
  const projects = useVisibleProjects(allProjects);
  const [loading, setLoading] = useState(true);
  const [bookingsMap, setBookingsMap] = useState({});
  const [approvedOvertimeRequests, setApprovedOvertimeRequests] = useState([]);
  const [approvedAbsenceRequests, setApprovedAbsenceRequests] = useState([]);
  const [payrollLocks, setPayrollLocks] = useState({ companyUid: null, ready: false, byPeriod: new Map() });

  useEffect(() => {
    const companyUid = activeCompany?.companyAccountUid;
    if (!companyUid) {
      setPayrollLocks({ companyUid: null, ready: false, byPeriod: new Map() });
      return;
    }

    setPayrollLocks({ companyUid, ready: false, byPeriod: new Map() });
    return onSnapshot(
      collection(db, "users", companyUid, "payrollApprovals"),
      (snapshot) => {
        const byPeriod = new Map();
        snapshot.docs.forEach((approvalDoc) => {
          const approval = approvalDoc.data();
          if (approval.approved !== true || !approval.period || !approval.employeeId) return;
          if (!byPeriod.has(approval.period)) byPeriod.set(approval.period, new Set());
          byPeriod.get(approval.period).add(String(approval.employeeId));
        });
        setPayrollLocks({ companyUid, ready: true, byPeriod });
      },
      (error) => {
        console.error("Kunde inte läsa godkända löneunderlag:", error);
        setPayrollLocks({ companyUid, ready: false, byPeriod: new Map() });
      }
    );
  }, [activeCompany?.companyAccountUid]);
 
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [placingTemplate, setPlacingTemplate] = useState(false);
  useEffect(() => { setSelectedTemplate(null); }, [selectedSchema, activeCompany?.companyAccountUid, showSheetsPopup]);
  const [draggedShift, setDraggedShift] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [dragOverRowId, setDragOverRowId] = useState(null);
 
  const [refreshKey, setRefreshKey] = useState(0);
 
  const forceRefreshShifts = () => {
    setRefreshKey((prev) => prev + 1);
  };
 
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.forceRefreshShiftPage = forceRefreshShifts;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete window.forceRefreshShiftPage;
      }
    };
  }, []);
 
  // Hämta publish settings med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!accountToUse) {
      setPublishedUntil(null);
      return;
    }
 
    if (shiftPageCache.publishLoaded && 
        shiftPageCache.publishCompanyUid === accountToUse) {
      setPublishedUntil(shiftPageCache.publishSettings);
      return;
    }
 
    const publishRef = collection(
      db,
      "users",
      accountToUse,
      "publishSettings"
    );
 
    const unsubscribe = onSnapshot(
      publishRef,
      (snapshot) => {
        let foundPublishedDate = null;
 
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.publishedUntil) {
            if (data.publishedUntil.toDate) {
              foundPublishedDate = data.publishedUntil.toDate();
            } else if (data.publishedUntil instanceof Date) {
              foundPublishedDate = data.publishedUntil;
            } else {
              foundPublishedDate = new Date(data.publishedUntil);
            }
          }
        });
 
        shiftPageCache.publishSettings = foundPublishedDate;
        shiftPageCache.publishCompanyUid = accountToUse;
        shiftPageCache.publishLoaded = true;
        shiftPageCache.publishUnsubscribe = unsubscribe;
 
        setPublishedUntil(foundPublishedDate);
      },
      (error) => {
        console.error("Fel vid hämtning av publiceringsdatum:", error);
      }
    );
 
    shiftPageCache.publishUnsubscribe = unsubscribe;
 
    return () => {};
  }, [activeCompany?.companyAccountUid]);
 
  // Hämta bookings med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!accountToUse) {
      setBookingsMap({});
      return;
    }
 
    if (shiftPageCache.bookingsLoaded && 
        shiftPageCache.bookingsCompanyUid === accountToUse) {
      setBookingsMap(shiftPageCache.bookings || {});
      return;
    }
 
    const bookingsRef = collection(db, "users", accountToUse, "bookings");
 
    // Hämta alla bokningar och filtrera i klienten. Appen kan spara status som
    // "booked" efter att deltagaren har bokat sig, inte endast "approved".
    const unsubscribe = onSnapshot(
      bookingsRef,
      (snapshot) => {
        const bookings = {};
 
        snapshot.forEach((docSnap) => {
          const bookingData = docSnap.data();
          const status = String(bookingData.status || "").toLowerCase();
 
          if (!["approved", "reserved", "booked"].includes(status)) return;
 
          const originalShiftId =
            bookingData.originalShiftId ||
            bookingData.shiftId ||
            bookingData.scheduleShiftId ||
            null;
 
          if (!originalShiftId) return;
 
          const participantIndex = Number.isInteger(bookingData.participantIndex)
            ? bookingData.participantIndex
            : Number.isFinite(Number(bookingData.participantIndex))
            ? Number(bookingData.participantIndex)
            : 0;
 
          const booking = {
            id: docSnap.id,
            originalShiftId,
            participantIndex,
            userId:
              bookingData.userId ||
              bookingData.employeeId ||
              bookingData.uid ||
              null,
            employeeId: bookingData.employeeId || null,
            uid: bookingData.uid || null,
            userEmail: bookingData.userEmail || bookingData.email || "",
            userName:
              bookingData.userName ||
              bookingData.employeeName ||
              bookingData.name ||
              "",
            status,
            bookedFromApplications: true,
            isApproved: status === "approved" || status === "booked",
            isReserved: status === "reserved",
          };
 
          // Ordinarie nyckel.
          bookings[`${originalShiftId}-${participantIndex}`] = booking;
        });
 
        shiftPageCache.bookings = bookings;
        shiftPageCache.bookingsCompanyUid = accountToUse;
        shiftPageCache.bookingsLoaded = true;
        shiftPageCache.bookingsUnsubscribe = unsubscribe;
 
        setBookingsMap(bookings);
      },
      (error) => {
        console.error("Fel vid hämtning av bokningar:", error);
      }
    );
 
    shiftPageCache.bookingsUnsubscribe = unsubscribe;
 
    return () => {};
  }, [activeCompany?.companyAccountUid]);
 
  // Hämta approved overtime requests med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!accountToUse) {
      setApprovedOvertimeRequests([]);
      return;
    }
 
    if (shiftPageCache.overtimeLoaded && 
        shiftPageCache.overtimeCompanyUid === accountToUse) {
      setApprovedOvertimeRequests(shiftPageCache.approvedOvertime || []);
      return;
    }
 
    const overtimeRequestsRef = collection(
      db,
      "users",
      accountToUse,
      "overtimeRequests"
    );
 
    const q = query(overtimeRequestsRef, where("status", "==", "approved"));
 
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
 
        shiftPageCache.approvedOvertime = requests;
        shiftPageCache.overtimeCompanyUid = accountToUse;
        shiftPageCache.overtimeLoaded = true;
        shiftPageCache.overtimeUnsubscribe = unsubscribe;
 
        setApprovedOvertimeRequests(requests);
      },
      (error) => {
        console.error("Fel vid hämtning av godkända övertidsansökningar:", error);
        setApprovedOvertimeRequests([]);
      }
    );
 
    shiftPageCache.overtimeUnsubscribe = unsubscribe;
 
    return () => {};
  }, [activeCompany?.companyAccountUid]);
 
  // Hämta approved absence requests med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!accountToUse) {
      setApprovedAbsenceRequests([]);
      return;
    }
 
    if (shiftPageCache.absenceLoaded && 
        shiftPageCache.absenceCompanyUid === accountToUse) {
      setApprovedAbsenceRequests(shiftPageCache.approvedAbsence || []);
      return;
    }
 
    const absenceRequestsRef = collection(
      db,
      "users",
      accountToUse,
      "absenceRequests"
    );
 
    const q = query(absenceRequestsRef, where("status", "==", "approved"));
 
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
 
        shiftPageCache.approvedAbsence = requests;
        shiftPageCache.absenceCompanyUid = accountToUse;
        shiftPageCache.absenceLoaded = true;
        shiftPageCache.absenceUnsubscribe = unsubscribe;
 
        setApprovedAbsenceRequests(requests);
      },
      (error) => {
        console.error("Fel vid hämtning av godkända frånvaroansökningar:", error);
        setApprovedAbsenceRequests([]);
      }
    );
 
    shiftPageCache.absenceUnsubscribe = unsubscribe;
 
    return () => {};
  }, [activeCompany?.companyAccountUid]);
 
  useEffect(() => {
    const companyUid = activeCompany?.companyAccountUid;
    if (!companyUid) {
      setOwnerEmployee(null);
      return;
    }
    setOwnerEmployee(null);
    return onSnapshot(doc(db, "users", companyUid), (snapshot) => {
      const data = snapshot.data();
      if (data?.role !== "super-admin") {
        setOwnerEmployee(null);
        return;
      }
      const name = [data.firstName, data.lastName].filter(Boolean).join(" ") ||
        data.companyName || data.email || "Företagsägare";
      setOwnerEmployee({ id: companyUid, name, email: data.email || "", role: "super-admin", isOwner: true, workTimeId: data.workTimeId || "" });
    }, (error) => {
      console.error("Fel vid hämtning av företagsägaren:", error);
      setOwnerEmployee(null);
    });
  }, [activeCompany?.companyAccountUid]);

  // Hämta employees med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!accountToUse) {
      setEmployees([]);
      setEmployeesReadyCompanyUid(null);
      setLoading(false);
      return;
    }
 
    if (shiftPageCache.employeesLoaded && 
        shiftPageCache.employeesCompanyUid === accountToUse) {
      setEmployees(shiftPageCache.employees || []);
      setEmployeesReadyCompanyUid(accountToUse);
      setLoading(false);
    } else {
      setEmployees([]);
      setEmployeesReadyCompanyUid(null);
    }
 
    const employedRef = collection(db, "users", accountToUse, "employed");
 
    const unsubscribe = onSnapshot(
      employedRef,
      (snapshot) => {
        const users = [];
 
        snapshot.forEach((userDoc) => {
          const userData = userDoc.data();
 
          let fullName = userData.name || "";
          if (userData.firstName && userData.lastName) {
            fullName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            fullName = userData.firstName;
          } else if (userData.lastName) {
            fullName = userData.lastName;
          } else if (userData.displayName) {
            fullName = userData.displayName;
          } else if (userData.userName) {
            fullName = userData.userName;
          }
 
          users.push({
            ...userData,
            id: userDoc.id,
            name: fullName || "Användare",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            email: userData.email || "",
          });
        });
 
        users.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        
        shiftPageCache.employees = users;
        shiftPageCache.employeesCompanyUid = accountToUse;
        shiftPageCache.employeesLoaded = true;
        shiftPageCache.employeesUnsubscribe = unsubscribe;
 
        setEmployees(users);
        setEmployeesReadyCompanyUid(accountToUse);
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av anställda:", error);
        setScheduleLoadErrorCompanyUid(accountToUse);
        setLoading(false);
      }
    );
 
    shiftPageCache.employeesUnsubscribe = unsubscribe;
 
    return () => {
      unsubscribe();
      if (shiftPageCache.employeesUnsubscribe === unsubscribe) {
        shiftPageCache.employeesUnsubscribe = null;
      }
    };
  }, [activeCompany?.companyAccountUid]);
 
  // Hämta projects med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!accountToUse) {
      setProjects([]);
      return;
    }
 
    if (shiftPageCache.projectsLoaded && 
        shiftPageCache.projectsCompanyUid === accountToUse) {
      setProjects(shiftPageCache.projects || []);
      return;
    }
 
    const projectsRef = collection(db, "users", accountToUse, "projects");
 
    const unsubscribe = onSnapshot(
      projectsRef,
      (snapshot) => {
        const projectList = [];
 
        snapshot.forEach((projectDoc) => {
          const data = projectDoc.data();
 
          projectList.push({
            id: projectDoc.id,
            name: data.name || "Projekt saknas",
          });
        });
 
        projectList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        
        shiftPageCache.projects = projectList;
        shiftPageCache.projectsCompanyUid = accountToUse;
        shiftPageCache.projectsLoaded = true;
        shiftPageCache.projectsUnsubscribe = unsubscribe;
 
        setProjects(projectList);
      },
      (error) => {
        console.error("Fel vid hämtning av projekt:", error);
      }
    );
 
    shiftPageCache.projectsUnsubscribe = unsubscribe;
 
    return () => {};
  }, [activeCompany?.companyAccountUid]);
 
  // Hämta ALLA pass från shifts.
  // Den andra sidan läser hela shifts-samlingen. ShiftPage måste göra samma sak,
  // eftersom pass från appen eller äldre pass kan sakna year/month-fälten.
  // Datumfiltreringen sker senare i vecka- och månadsvyn via shift.date.
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!accountToUse) {
      setShifts([]);
      return;
    }
 
    console.log("📅 Hämtar alla pass till ShiftPage...");
 
    const shiftsRef = collection(db, "users", accountToUse, "shifts");
 
    const unsubscribe = onSnapshot(
      shiftsRef,
      (snapshot) => {
        const shiftsList = snapshot.docs.map((shiftDoc) => {
          const shiftData = shiftDoc.data();
          const parsedDate = safeParseDate(shiftData.date);
 
          return {
            id: shiftDoc.id,
            ...shiftData,
            year:
              shiftData.year ??
              (parsedDate ? parsedDate.getFullYear() : null),
            month:
              shiftData.month ??
              (parsedDate ? parsedDate.getMonth() + 1 : null),
          };
        });
 
        console.log(`✅ Hittade totalt ${shiftsList.length} pass i shifts`);
        setShifts(shiftsList);
      },
      (error) => {
        console.error("Fel vid hämtning av alla pass:", error);
        setShifts([]);
      }
    );
 
    if (shiftPageCache.shiftsUnsubscribe) {
      shiftPageCache.shiftsUnsubscribe();
    }
 
    shiftPageCache.shiftsCompanyUid = accountToUse;
    shiftPageCache.shiftsUnsubscribe = unsubscribe;
 
    return () => {
      unsubscribe();
 
      if (shiftPageCache.shiftsUnsubscribe === unsubscribe) {
        shiftPageCache.shiftsUnsubscribe = null;
      }
    };
  }, [activeCompany?.companyAccountUid, refreshKey]);
 
  // Hämta schemas med cache
  useEffect(() => {
    const accountToUse = activeCompany?.companyAccountUid;
 
    if (!accountToUse) {
      setSchemas([]);
      setSchemasReadyCompanyUid(null);
      return;
    }

    setSchemasReadyCompanyUid(null);
    setScheduleLoadErrorCompanyUid(null);
 
    const schedulingRef = collection(db, "users", accountToUse, "Scheduling");
 
    const unsubscribe = onSnapshot(
      schedulingRef,
      (snapshot) => {
        const schemaList = readSchedulingSchemas(snapshot);
        
        shiftPageCache.schemas = schemaList;
        shiftPageCache.schemasCompanyUid = accountToUse;
        shiftPageCache.schemasLoaded = true;
        shiftPageCache.schemasUnsubscribe = unsubscribe;
 
        setSchemas(schemaList);
        setSchemasReadyCompanyUid(accountToUse);
      },
      (error) => {
        console.error("Fel vid hämtning av scheman:", error);
        setScheduleLoadErrorCompanyUid(accountToUse);
      }
    );
 
    shiftPageCache.schemasUnsubscribe = unsubscribe;
 
    return () => {
      unsubscribe();
      if (shiftPageCache.schemasUnsubscribe === unsubscribe) shiftPageCache.schemasUnsubscribe = null;
    };
  }, [activeCompany?.companyAccountUid, refreshKey]);
 
  // Rensa cache när användaren loggar ut
  useEffect(() => {
    if (!currentUser) {
      console.log("👋 Användare loggade ut - rensar cache");
      
      if (shiftPageCache.schemasUnsubscribe) {
        shiftPageCache.schemasUnsubscribe();
        shiftPageCache.schemasUnsubscribe = null;
      }
      if (shiftPageCache.shiftsUnsubscribe) {
        shiftPageCache.shiftsUnsubscribe();
        shiftPageCache.shiftsUnsubscribe = null;
      }
      if (shiftPageCache.employeesUnsubscribe) {
        shiftPageCache.employeesUnsubscribe();
        shiftPageCache.employeesUnsubscribe = null;
      }
      if (shiftPageCache.projectsUnsubscribe) {
        shiftPageCache.projectsUnsubscribe();
        shiftPageCache.projectsUnsubscribe = null;
      }
      if (shiftPageCache.bookingsUnsubscribe) {
        shiftPageCache.bookingsUnsubscribe();
        shiftPageCache.bookingsUnsubscribe = null;
      }
      if (shiftPageCache.overtimeUnsubscribe) {
        shiftPageCache.overtimeUnsubscribe();
        shiftPageCache.overtimeUnsubscribe = null;
      }
      if (shiftPageCache.absenceUnsubscribe) {
        shiftPageCache.absenceUnsubscribe();
        shiftPageCache.absenceUnsubscribe = null;
      }
      if (shiftPageCache.publishUnsubscribe) {
        shiftPageCache.publishUnsubscribe();
        shiftPageCache.publishUnsubscribe = null;
      }
 
      shiftPageCache.schemas = null;
      shiftPageCache.schemasCompanyUid = null;
      shiftPageCache.schemasLoaded = false;
      shiftPageCache.shiftsCompanyUid = null;
      shiftPageCache.employees = null;
      shiftPageCache.employeesCompanyUid = null;
      shiftPageCache.employeesLoaded = false;
      shiftPageCache.projects = null;
      shiftPageCache.projectsCompanyUid = null;
      shiftPageCache.projectsLoaded = false;
      shiftPageCache.bookings = null;
      shiftPageCache.bookingsCompanyUid = null;
      shiftPageCache.bookingsLoaded = false;
      shiftPageCache.approvedOvertime = null;
      shiftPageCache.overtimeCompanyUid = null;
      shiftPageCache.overtimeLoaded = false;
      shiftPageCache.approvedAbsence = null;
      shiftPageCache.absenceCompanyUid = null;
      shiftPageCache.absenceLoaded = false;
      shiftPageCache.publishSettings = null;
      shiftPageCache.publishCompanyUid = null;
      shiftPageCache.publishLoaded = false;
    }
  }, [currentUser]);
 
  const schemaOptions = useMemo(() => {
    if (activeCompany?.role === "admin" && permissionsLoading) return [];
    return allowedSchemaIds === null ? schemas : schemas.filter((schema) => allowedSchemaIds.includes(schema.id));
  }, [schemas, allowedSchemaIds, activeCompany?.role, permissionsLoading]);
  const selectedSchemaDetails = schemaOptions.find((schema) => schema.id === selectedSchema);
  const visibleEmployeeIds = selectedSchemaDetails?.visibleEmployeeIds;
  const visibleEmployees = useMemo(() => {
    if (!selectedSchemaDetails) return [];
    return Array.isArray(visibleEmployeeIds)
      ? employees.filter((employee) => visibleEmployeeIds.includes(employee.id))
      : employees;
  }, [employees, selectedSchemaDetails, visibleEmployeeIds]);

  useEffect(() => {
    if (!selectedSchemaDetails) return;
    if (selectedUserId && !visibleEmployees.some((employee) => employee.id === selectedUserId)) {
      setSelectedUserId("");
      setSelectedUser("");
    }
  }, [selectedUserId, selectedSchemaDetails, visibleEmployees]);

  useEffect(() => {
    if (!schemaOptions.length) {
      setSelectedSchema("");
      return;
    }
 
    const exists = schemaOptions.some((schema) => schema.id === selectedSchema);
 
    if (!selectedSchema || !exists) {
      setSelectedSchema(schemaOptions[0].id);
    }
  }, [schemaOptions, selectedSchema]);
 
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target) &&
        dateDisplayRef.current &&
        !dateDisplayRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    }
 
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
 
  const isDatePublished = (date) => {
    if (!publishedUntil) return false;
    if (!date) return false;
 
    try {
      const shiftDate = safeParseDate(date);
      if (!shiftDate) return false;
 
      const publishedDate = new Date(publishedUntil);
      publishedDate.setHours(23, 59, 59, 999);
 
      const compareDate = new Date(shiftDate);
      compareDate.setHours(0, 0, 0, 0);
 
      return compareDate <= publishedDate;
    } catch (error) {
      console.error("Fel vid kontroll av publicerat datum:", error);
      return false;
    }
  };
 
  const moveShiftToDate = async (shiftToMove, targetDate, targetRow = null) => {
    if (!shiftToMove || !targetDate || !activeCompany?.companyAccountUid) return;
    if (isShiftInteractionBlocked(shiftToMove)) return;
 
    const isShiftPublished = isDatePublished(shiftToMove.date);
    const isTargetPublished = isDatePublished(targetDate);
 
    if (isShiftPublished && !isTargetPublished) {
      alert("Kan inte flytta ett publicerat pass till ett opublicerat datum.");
      return;
    }
 
    if (!isShiftPublished && isTargetPublished) {
      alert("Kan inte flytta ett opublicerat pass till ett publicerat datum.");
      return;
    }
 
    try {
      const originalShift = shifts.find((s) => s.id === shiftToMove.originalId);
 
      if (originalShift) {
        const shiftRef = doc(
          db,
          "users",
          activeCompany.companyAccountUid,
          "shifts",
          shiftToMove.originalId
        );
 
        const targetDateObj = safeParseDate(targetDate);
        const year = targetDateObj ? targetDateObj.getFullYear() : null;
        const month = targetDateObj ? targetDateObj.getMonth() + 1 : null;
 
        const update = {
          date: targetDate,
          year: year,
          month: month,
          updatedAt: serverTimestamp(),
        };

        // In the employee view, dropping onto a different row also reassigns
        // the dragged participant while keeping their time and payroll data.
        if (targetRow && !targetRow.isProject) {
          const participantIndex = Number.isInteger(shiftToMove.participantIndex)
            ? shiftToMove.participantIndex
            : 0;
          const currentParticipants = Array.isArray(originalShift.employees) && originalShift.employees.length
            ? originalShift.employees
            : [{
                employeeId: originalShift.employeeId || null,
                name: originalShift.employee || originalShift.employeeName || "",
                isObemannad: Boolean(originalShift.isObemannad),
              }];
          const existingParticipant = currentParticipants[participantIndex];
          const targetEmployee = targetRow.isObemannad ? null : targetRow.employee;
          if (existingParticipant && (targetEmployee || targetRow.isObemannad)) {
            if (targetEmployee && isEmployeePayrollLocked(targetEmployee.id, targetDate)) {
              alert("Kan inte flytta passet till en person med låst löneunderlag.");
              return;
            }
            const nextParticipant = {
              ...existingParticipant,
              employeeId: targetEmployee?.id || null,
              name: targetEmployee?.name || targetEmployee?.userName || "Obemannad",
              isObemannad: Boolean(targetRow.isObemannad),
            };
            const nextEmployees = currentParticipants.map((participant, index) =>
              index === participantIndex ? nextParticipant : participant
            );
            update.employees = nextEmployees;
            update.employee = nextEmployees[0]?.name || "";
            update.employeeId = nextEmployees[0]?.employeeId || null;
            update.isObemannad = nextEmployees.some((participant) => participant.isObemannad);
            update.isSelfBookable = update.isObemannad;
            update.bookingType = update.isObemannad ? "self_booking" : null;
          }
        }

        await updateDoc(shiftRef, update);
 
        forceRefreshShifts();
      }
    } catch (error) {
      console.error("Fel vid flytt av pass:", error);
      alert("Kunde inte flytta passet. Försök igen.");
    }
  };
 
  const handleDragStart = (e, shift) => {
    if (isShiftInteractionBlocked(shift)) {
      e.preventDefault();
      return;
    }
    setDraggedShift(shift);
    e.dataTransfer.setData("text/plain", shift.id);
    e.dataTransfer.effectAllowed = "move";
 
    if (e.target) {
      e.dataTransfer.setDragImage(e.target, 20, 20);
    }
  };
 
  const handleDragEnd = () => {
    setDraggedShift(null);
    setDragOverDate(null);
    setDragOverRowId(null);
  };
 
  const handleDragOver = (e, date, targetRow = null) => {
    e.preventDefault();

    const isProjectDrag = Array.from(e.dataTransfer.types || []).includes(
      "application/x-schema-project"
    );

    if (isProjectDrag && date) {
      e.dataTransfer.dropEffect = "copy";
      if (!dragOverDate || !isSameDay(dragOverDate, date)) {
        setDragOverDate(date);
      }
      setDragOverRowId(targetRow?.id || null);
      return;
    }
 
    if (!draggedShift || !date) return;
 
    const isShiftPublished = isDatePublished(draggedShift.date);
    const isTargetPublished = isDatePublished(date);
 
    if (isShiftPublished === isTargetPublished) {
      e.dataTransfer.dropEffect = "move";
      if (dragOverDate !== date) {
        setDragOverDate(date);
      }
      setDragOverRowId(targetRow?.id || null);
    } else {
      e.dataTransfer.dropEffect = "none";
    }
  };
 
  const handleDragLeave = () => {
    setDragOverDate(null);
    setDragOverRowId(null);
  };
 
  const handleDrop = async (e, targetDate, targetRow = null) => {
    e.preventDefault();

    const projectPayload = e.dataTransfer.getData(
      "application/x-schema-project"
    );

    if (projectPayload) {
      if (!schemas.length || !selectedSchema) {
        setDragOverDate(null);
        openSchemaRequiredPopup();
        return;
      }

      try {
        const project = JSON.parse(projectPayload);
        const parsedDate = safeParseDate(targetDate);
        const startTime = String(project?.startTime || "");
        const endTime = String(project?.endTime || "");

        if (!project?.id || !project?.name || !parsedDate) return;
        if (!/^\d{2}:\d{2}$/.test(startTime) || !/^\d{2}:\d{2}$/.test(endTime)) {
          alert("Välj både starttid och sluttid innan du drar projektet.");
          return;
        }

        const year = parsedDate.getFullYear();
        const month = parsedDate.getMonth() + 1;
        const day = parsedDate.getDate();
        const dateString = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const shiftsRef = collection(
          db,
          "users",
          activeCompany.companyAccountUid,
          "shifts"
        );

        const selectedMonthEmployee = !targetRow && selectedUserId
          ? findEmployeeByIdentifier(selectedUserId)
          : null;
        const templateEmployee = project.templateEmployeeId ? findEmployeeByIdentifier(project.templateEmployeeId) : null;
        const targetEmployee = targetRow?.employee || templateEmployee || selectedMonthEmployee;
        const saveAsObemannad = targetRow
          ? Boolean(targetRow.isObemannad || targetRow.isProject)
          : !targetEmployee;
        const employeeName = saveAsObemannad
          ? "Obemannad"
          : targetEmployee?.name || targetEmployee?.userName || "Användare";
        const employeeId = saveAsObemannad ? "" : targetEmployee?.id || "";

        if (!payrollLocksReady || (employeeId && isEmployeePayrollLocked(employeeId, targetDate))) return;

        await addDoc(shiftsRef, {
          schemaId: selectedSchema,
          project: project.name,
          projectId: project.id,
          date: dateString,
          year,
          month,
          startTime,
          endTime,
          time: `${startTime} - ${endTime}`,
          employees: [
            {
              employeeId,
              name: employeeName,
              isObemannad: saveAsObemannad,
              overtime: [],
              absences: [],
              breaks: [],
              isBooked: false,
              isApproved: false,
              bookingId: null,
              originalEmployeeId: null,
            },
          ],
          employee: employeeName,
          employeeId: employeeId || null,
          isObemannad: saveAsObemannad,
          isSelfBookable: saveAsObemannad,
          bookingType: saveAsObemannad ? "self_booking" : null,
          message: "",
          published: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: currentUser?.uid || null,
        });
      } catch (error) {
        console.error("Kunde inte lägga projektet i schemat:", error);
        alert("Kunde inte lägga projektet i schemat. Försök igen.");
      } finally {
        setDragOverDate(null);
        setDragOverRowId(null);
      }
      return;
    }
 
    if (!draggedShift) return;
 
    await moveShiftToDate(draggedShift, targetDate, targetRow);
 
    setDraggedShift(null);
    setDragOverDate(null);
    setDragOverRowId(null);
  };
 
  const handleBookingsIconClick = (e, shift) => {
    e.stopPropagation();
    if (isShiftInteractionBlocked(shift)) return;
    setSelectedShiftForBookings(shift);
    setShowBookingsPopup(true);
  };
 
 
  const weekDays = useMemo(() => {
    if (!currentDate) return [];
 
    try {
      const monday = getMonday(currentDate);

      // Visa alltid en hel kalendervecka, även när veckan går över
      // månadsskiftet. Då behövs ingen extra bläddring för nästa månad.
      return Array.from({ length: 7 }, (_, index) => {
        const day = new Date(monday);
        day.setDate(monday.getDate() + index);
        return day;
      });
    } catch (error) {
      console.error("Fel vid skapande av veckodagar:", error);
      return [];
    }
  }, [currentDate]);
 
  const monthDays = useMemo(() => {
    return getMonthDays(currentDate);
  }, [currentDate]);
 
  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((emp) => {
      map[emp.id] = emp.name || emp.userName || "Användare";
    });
    return map;
  }, [employees]);
 
  const normalizeIdentifier = (value) =>
    String(value || "").trim().toLowerCase();
 
  const findEmployeeByIdentifier = (identifier) => {
    const normalized = normalizeIdentifier(identifier);
    if (!normalized) return null;
 
    return (
      employees.find((emp) =>
        [emp.id, emp.uid, emp.userId, emp.employeeId, emp.email]
          .map(normalizeIdentifier)
          .includes(normalized)
      ) || null
    );
  };
 
  const findBookingForShiftRow = (shift, participantIndex = 0) => {
    const shiftIds = [shift?.id, shift?.originalId, shift?.shiftId]
      .filter(Boolean)
      .map(String);
 
    for (const shiftId of shiftIds) {
      const exact = bookingsMap[`${shiftId}-${participantIndex}`];
      if (exact) return exact;
    }
 
    return null;
  };
 
  // Visar vanliga schemapass, obemannade pass och bokade app-pass
  // som tillhör det valda schemat.
  const expandedShifts = useMemo(() => {
    const expanded = [];
    if (!schemaOptions.some((schema) => schema.id === selectedSchema)) return expanded;
 
    shifts.forEach((shift) => {
      // Varje pass sparas med schemaId i PopupShift. Filtrera här så att ett
      // pass aldrig följer med när användaren byter till ett annat schema.
      if (shift.schemaId !== selectedSchema || shift.approvedUnstaffedPlaceholder === true) {
        return;
      }

      const shiftEmployees = Array.isArray(shift.employees) ? shift.employees : [];
      // Skapa minst en deltagarrad även när app-passet inte har employees-array.
      const rows = shiftEmployees.length > 0
        ? shiftEmployees
        : [
            {
              employeeId: shift.employeeId || shift.userId || shift.uid || null,
              name: shift.employeeName || shift.employee || shift.userName || "",
              email: shift.employeeEmail || shift.userEmail || shift.email || "",
              isObemannad: shift.isObemannad === true,
              breaks: shift.breaks || [],
              overtime: shift.overtime || [],
              absences: shift.absences || [],
            },
          ];

      let approvedUnstaffedPlaceholderAdded = false;
      rows.forEach((employeeData, index) => {
        const bookingInfo = findBookingForShiftRow(shift, index);
        const bookingStatus = String(bookingInfo?.status || "").toLowerCase();
        const rowIsBooked =
          Boolean(bookingInfo) && ["approved", "booked"].includes(bookingStatus);
        const hasReservation = Boolean(bookingInfo) && bookingStatus === "reserved";
 
        const rawEmployeeIdentifier = rowIsBooked
          ? bookingInfo.userId ||
            bookingInfo.employeeId ||
            bookingInfo.uid ||
            bookingInfo.userEmail
          : employeeData?.employeeId ||
            employeeData?.userId ||
            employeeData?.uid ||
            employeeData?.email ||
            shift.employeeId ||
            shift.userId ||
            shift.uid ||
            shift.employeeEmail ||
            shift.userEmail ||
            null;
 
        const matchedEmployee = findEmployeeByIdentifier(rawEmployeeIdentifier);
        const effectiveEmployeeId =
          matchedEmployee?.id || rawEmployeeIdentifier || null;
 
        const employeeName = rowIsBooked
          ? bookingInfo.userName ||
            matchedEmployee?.name ||
            matchedEmployee?.userName ||
            employeeData?.name ||
            "Användare"
          : matchedEmployee?.name ||
            employeeData?.name ||
            employeeData?.employee ||
            shift.employeeName ||
            shift.employee ||
            (employeeData?.isObemannad || shift.isObemannad ? "Obemannad" : "Okänd");
 
        const isObemannad = rowIsBooked
          ? false
          : Boolean(employeeData?.isObemannad || shift.isObemannad);

        if (!isObemannad && Array.isArray(visibleEmployeeIds) &&
            !visibleEmployeeIds.includes(effectiveEmployeeId)) return;
 
        // Visa raden om den är bokad, reserverad, bemannad eller obemannad.
        if (!rowIsBooked && !hasReservation && !effectiveEmployeeId && !isObemannad) {
          return;
        }
 
        const baseEmployeeData = {
          ...employeeData,
          payrollTime: employeeData?.payrollTime || (!shift.employees?.length ? shift.payrollTime : null),
          employeeId: effectiveEmployeeId,
          isObemannad,
          name: employeeName,
          breaks: employeeData?.breaks || shift.breaks || [],
          overtime: employeeData?.overtime || shift.overtime || [],
          absences: employeeData?.absences || shift.absences || [],
        };
 
        const mergedEmployeeData = mergeApprovedRequestsIntoEmployeeData({
          shift,
          employeeData: baseEmployeeData,
          employeeId: effectiveEmployeeId,
          approvedOvertimeRequests,
          approvedAbsenceRequests,
          employees,
        });
 
        expanded.push({
          ...resolvePayrollTime(shift, mergedEmployeeData).shift,
          id: `${shift.id}-${index}`,
          originalId: shift.id,
          participantIndex: index,
          employeeId: effectiveEmployeeId,
          employeeIdentifiers: [
            effectiveEmployeeId,
            rawEmployeeIdentifier,
            ...(rowIsBooked
              ? [
                  bookingInfo?.userId,
                  bookingInfo?.employeeId,
                  bookingInfo?.uid,
                  bookingInfo?.userEmail,
                ]
              : []),
            matchedEmployee?.id,
            matchedEmployee?.uid,
            matchedEmployee?.userId,
            matchedEmployee?.email,
          ].filter(Boolean),
          employee: employeeName,
          employeeName,
          isObemannad,
          badges: getDetailedBadges(mergedEmployeeData),
          employeeData: resolvePayrollTime(shift, mergedEmployeeData).employeeData,
          bookedFromApplications: rowIsBooked,
          hasReservation,
          source: bookingInfo ? "applications" : null,
          hasMessage: Boolean(shift.message || employeeData?.message),
          hasBookings: Boolean(bookingInfo),
        });

        if (rowIsBooked && !approvedUnstaffedPlaceholderAdded) {
          approvedUnstaffedPlaceholderAdded = true;
          // Referenskortet hör enbart till raden Obemannad.
          expanded.push({
            ...shift,
            id: `${shift.id}-approved-unstaffed`,
            originalId: shift.id,
            participantIndex: index,
            employeeId: null,
            employeeIdentifiers: [],
            employee: "Obemannad",
            employeeName: "Obemannad",
            isObemannad: true,
            approvedUnstaffedPlaceholder: true,
            employeeData: { employeeId: null, name: "Obemannad", isObemannad: true },
            badges: [],
            bookedFromApplications: false,
            hasReservation: false,
            hasBookings: true,
          });
        }
      });
    });
 
    return expanded;
  }, [
    shifts,
    selectedSchema,
    schemaOptions,
    employees,
    visibleEmployeeIds,
    bookingsMap,
    approvedOvertimeRequests,
    approvedAbsenceRequests,
  ]);

  const notifications = useScheduleNotifications({
    companyUid: activeCompany?.companyAccountUid,
    userId: currentUser?.uid,
    schemaId: selectedSchema,
    employees: visibleEmployees,
    shifts: expandedShifts,
    workTimes: workTimeMeasures.companyUid === activeCompany?.companyAccountUid ? workTimeMeasures.byId : {},
    currentDate,
    isOpen: showNotificationsPopup,
    dataReady: !companyLoading && !permissionsLoading &&
      schemasReadyCompanyUid === activeCompany?.companyAccountUid &&
      employeesReadyCompanyUid === activeCompany?.companyAccountUid &&
      workTimeMeasures.companyUid === activeCompany?.companyAccountUid && Boolean(selectedSchemaDetails),
  });

  const unstaffedShifts = expandedShifts
    .filter(
      (shift) =>
        shift.isObemannad === true &&
        shift.approvedUnstaffedPlaceholder !== true
    )
    .filter(
      (shift, index, list) =>
        list.findIndex(
          (candidate) =>
            (candidate.originalId || candidate.id) ===
            (shift.originalId || shift.id)
        ) === index
    )
    .sort((left, right) => {
      const leftDate = safeParseDate(left.date);
      const rightDate = safeParseDate(right.date);
      if (!leftDate && !rightDate) return 0;
      if (!leftDate) return 1;
      if (!rightDate) return -1;
      return leftDate - rightDate;
    });

  const payrollLocksReady = payrollLocks.ready &&
    payrollLocks.companyUid === activeCompany?.companyAccountUid;

  const isShiftPayrollLocked = (shift) => {
    const shiftDate = safeParseDate(shift?.date);
    if (!shiftDate || !payrollLocksReady) return false;
    const period = `${shiftDate.getFullYear()}-${String(shiftDate.getMonth() + 1).padStart(2, "0")}`;
    const approvedEmployeeIds = payrollLocks.byPeriod.get(period);
    if (!approvedEmployeeIds?.size) return false;

    const originalId = shift.originalId || shift.id;
    const originalShift = shifts.find((entry) => entry.id === originalId) || shift;
    const participantIds = [
      originalShift.employeeId,
      originalShift.userId,
      originalShift.uid,
      ...(originalShift.employees || []).flatMap((person) => [person.employeeId, person.userId, person.uid]),
      ...Object.entries(bookingsMap)
        .filter(([key]) => key.startsWith(`${originalId}-`))
        .flatMap(([, booking]) => [booking?.userId, booking?.employeeId, booking?.uid]),
      ...expandedShifts
        .filter((entry) => (entry.originalId || entry.id) === originalId)
        .flatMap((entry) => [entry.employeeId, ...(entry.employeeIdentifiers || [])]),
    ];

    return participantIds.some((id) => id && approvedEmployeeIds.has(String(id)));
  };

  const isShiftInteractionBlocked = (shift) => !payrollLocksReady || isShiftPayrollLocked(shift);

  const isEmployeePayrollLocked = (employeeId, date) => {
    const shiftDate = safeParseDate(date);
    if (!payrollLocksReady || !employeeId || !shiftDate) return false;
    const period = `${shiftDate.getFullYear()}-${String(shiftDate.getMonth() + 1).padStart(2, "0")}`;
    const approvedEmployeeIds = payrollLocks.byPeriod.get(period);
    const matchedEmployeeId = findEmployeeByIdentifier(employeeId)?.id;
    return Boolean(approvedEmployeeIds?.has(String(matchedEmployeeId || employeeId)));
  };

  const filteredShifts = expandedShifts.filter((shift) => {
    const employeeName = shift.employeeName || shift.employee || "";
 
    const employeeMatch =
      !employeeFilter ||
      employeeName.toLowerCase().includes(employeeFilter.toLowerCase());
 
    const projectMatch =
      !projectFilter ||
      String(shift.project || "")
        .toLowerCase()
        .includes(projectFilter.toLowerCase());
 
    const selectedEmployee = selectedUserId
      ? findEmployeeByIdentifier(selectedUserId)
      : null;
 
    const selectedIdentifiers = [
      selectedUserId,
      selectedEmployee?.id,
      selectedEmployee?.uid,
      selectedEmployee?.userId,
      selectedEmployee?.employeeId,
      selectedEmployee?.email,
    ]
      .filter(Boolean)
      .map(normalizeIdentifier);
 
    const shiftIdentifiers = [
      shift.employeeId,
      ...(shift.employeeIdentifiers || []),
      shift.employeeData?.employeeId,
      shift.employeeData?.uid,
      shift.employeeData?.userId,
      shift.employeeData?.email,
    ]
      .filter(Boolean)
      .map(normalizeIdentifier);
 
    const userMatch =
      !selectedUserId ||
      selectedIdentifiers.some((identifier) =>
        shiftIdentifiers.includes(identifier)
      );
 
    return employeeMatch && projectMatch && userMatch;
  });
 
  const changeWeek = (direction) => {
    if (!currentDate) {
      setCurrentDate(new Date());
      return;
    }
 
    try {
      const next = new Date(currentDate);
      if (viewMode === "week") {
        next.setDate(next.getDate() + direction * 7);
      } else {
        next.setDate(1);
        next.setMonth(next.getMonth() + direction);
      }
      setCurrentDate(next);
    } catch (error) {
      console.error("Fel vid ändring av vecka/månad:", error);
    }
  };
 
  const closeShiftPopup = () => {
    setShowCreatePopup(false);
    setSelectedPopupDate(null);
    setSelectedShift(null);
    setPopupPreselectedEmployee(null);
    setPopupPreselectedObemannad(false);
    setPopupPreselectedProject(null);
    forceRefreshShifts();
  };
 
  const openSchemaRequiredPopup = () => {
    setShowSchemaRequiredPopup(true);
  };
 
  const handleSheetsClick = () => {
    setShowNotificationsPopup(false);
    setShowUnstaffedPanel(false);
    setShowSheetsPopup(true);
  };

  const handleQuickDeleteShift = async (event, shift) => {
    event.stopPropagation();
    if (isShiftInteractionBlocked(shift)) return;
    const shiftDocumentId = shift?.originalId || shift?.id;
    if (!isQuickDeleteMode || !shiftDocumentId || deletingShiftIds.has(shiftDocumentId)) return;
    setDeletingShiftIds((current) => new Set(current).add(shiftDocumentId));
    try {
      await deleteDoc(doc(db, "users", activeCompany.companyAccountUid, "shifts", shiftDocumentId));
      setShifts((current) => current.filter((currentShift) => currentShift.id !== shiftDocumentId));
    } catch (error) {
      console.error("Kunde inte radera passet:", error);
      window.alert("Passet kunde inte raderas. Försök igen.");
    } finally {
      setDeletingShiftIds((current) => {
        const next = new Set(current);
        next.delete(shiftDocumentId);
        return next;
      });
    }
  };

  const handleUnstaffedClick = () => {
    setShowNotificationsPopup(false);
    setShowSheetsPopup(false);
    setShowUnstaffedPanel(true);
  };
 
  const handleAddShiftClick = (
    date,
    employee = null,
    createAsObemannad = false,
    project = null
  ) => {
    if (!schemas.length || !selectedSchema) {
      openSchemaRequiredPopup();
      return;
    }
    const targetEmployee = employee || (!createAsObemannad && !project && selectedUserId
      ? findEmployeeByIdentifier(selectedUserId)
      : null);
    if (!payrollLocksReady || isEmployeePayrollLocked(targetEmployee?.id, date)) return;
 
    if (selectedTemplate) {
      if (placingTemplate) return;
      setPlacingTemplate(true);
      const targetRow = employee || createAsObemannad || project ? { employee, isObemannad: createAsObemannad, isProject: Boolean(project && !employee) } : null;
      handleDrop({ preventDefault() {}, dataTransfer: { getData: (type) => type === "application/x-schema-project" ? JSON.stringify(selectedTemplate) : "" } }, date, targetRow).finally(() => setPlacingTemplate(false));
      return;
    }
    setSelectedShift(null);
    setPopupPreselectedEmployee(employee);
    setPopupPreselectedObemannad(createAsObemannad);
    setPopupPreselectedProject(
      project || (employee && !createAsObemannad
        ? projects.find(item => item.id === MY_COMPANY_PROJECT_ID) || null
        : null)
    );
    
    let dateCopy;
    if (date instanceof Date) {
      dateCopy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    } else {
      const parsed = safeParseDate(date);
      if (parsed) {
        dateCopy = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      } else {
        dateCopy = null;
      }
    }
    
    setSelectedPopupDate(dateCopy);
    setShowCreatePopup(true);
  };
 
  const handleTopAddShiftClick = () => {
    if (!schemas.length || !selectedSchema) {
      openSchemaRequiredPopup();
      return;
    }
 
    setSelectedShift(null);
    setPopupPreselectedEmployee(null);
    setPopupPreselectedObemannad(false);
    setPopupPreselectedProject(null);
    setSelectedPopupDate(null);
    setShowCreatePopup(true);
  };
 
  const handleEditShiftClick = (shift) => {
    if (isShiftInteractionBlocked(shift)) return;
    setPopupPreselectedEmployee(null);
    setPopupPreselectedObemannad(false);
    setPopupPreselectedProject(null);
    const originalShift = shifts.find((s) => s.id === shift.originalId);
 
    if (originalShift) {
      setSelectedShift(originalShift);
      const shiftDate = safeParseDate(originalShift.date);
      if (shiftDate) {
        const cleanDate = new Date(shiftDate.getFullYear(), shiftDate.getMonth(), shiftDate.getDate());
        setSelectedPopupDate(cleanDate);
      } else {
        setSelectedPopupDate(null);
      }
      setShowCreatePopup(true);
    } else {
      setSelectedShift(shift);
      const shiftDate = safeParseDate(shift.date);
      if (shiftDate) {
        const cleanDate = new Date(shiftDate.getFullYear(), shiftDate.getMonth(), shiftDate.getDate());
        setSelectedPopupDate(cleanDate);
      } else {
        setSelectedPopupDate(null);
      }
      setShowCreatePopup(true);
    }
  };
 
  const handleDateSelect = (date) => {
    setCurrentDate(date);
    setShowDatePicker(false);
  };
 
  const handlePublish = (publishedDate) => {
    setPublishedUntil(publishedDate);
    setShowPublishPopup(false);
  };
 
  const handleUserSelect = (e) => {
    const selectedName = e.target.value;
    setSelectedUser(selectedName);
 
    const selectedEmployee = visibleEmployees.find((emp) => emp.name === selectedName);
    if (selectedEmployee) {
      setSelectedUserId(selectedEmployee.id);
    } else {
      setSelectedUserId("");
    }
  };
 
  const BadgeGroup = ({ badges }) => {
    if (!badges || badges.length === 0) return null;
 
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          marginTop: "8px",
          minWidth: 0,
        }}
      >
        {badges.map((badge, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "6px",
              minWidth: 0,
              maxWidth: "100%",
              boxSizing: "border-box",
              overflowWrap: "anywhere",
              fontSize: "11px",
              padding: "4px 8px",
              borderRadius: "6px",
              background: badge.bgColor,
              color: badge.color,
              border: `1px solid ${badge.borderColor}`,
              fontWeight: 500,
              lineHeight: 1.3,
            }}
          >
            <span style={{ minWidth: 0, fontWeight: 600 }}>{badge.label}:</span>
            <span style={{ minWidth: 0 }}>{badge.time}</span>
          </div>
        ))}
      </div>
    );
  };
 
  const renderWeek = () => {
    const dayNames = [
      "MÅNDAG",
      "TISDAG",
      "ONSDAG",
      "TORSDAG",
      "FREDAG",
      "LÖRDAG",
      "SÖNDAG",
    ];
 
    const displayedEmployees = visibleEmployees
      .filter((employee) => {
        if (!selectedUserId) return true;
        return normalizeIdentifier(employee.id) === normalizeIdentifier(selectedUserId);
      })
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "sv"));

    const employeeRows = [
      {
        id: "__unstaffed__",
        name: "Obemannad",
        employee: null,
        isObemannad: true,
      },
      ...displayedEmployees.map((employee) => ({
        id: employee.id,
        name: employee.name || employee.userName || "Användare",
        employee,
        isObemannad: false,
      })),
    ];

    const hasShiftsWithoutProject = filteredShifts.some(
      (shift) => !shift.projectId && !String(shift.project || "").trim()
    );
    const projectRows = [
      ...projects
        .filter((project) => {
          if (!projectFilter) return true;
          return String(project.name || "")
            .toLowerCase()
            .includes(projectFilter.toLowerCase());
        })
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "sv")
        )
        .map((project) => ({
          id: `project-${project.id}`,
          name: project.name || "Namnlöst projekt",
          project,
          employee: null,
          isObemannad: false,
          isProject: true,
        })),
      ...(hasShiftsWithoutProject
        ? [{
            id: "__without_project__",
            name: "Projekt saknas",
            project: null,
            employee: null,
            isObemannad: false,
            isProject: true,
          }]
        : []),
    ];
    const weekRows = weekRowMode === "projects" ? projectRows : employeeRows;

    const shiftBelongsToRow = (shift, row) => {
      if (shift.approvedUnstaffedPlaceholder) return Boolean(row.isObemannad);
      if (row.isProject) {

        if (!row.project) {
          return !shift.projectId && !String(shift.project || "").trim();
        }

        return (
          (shift.projectId && shift.projectId === row.project.id) ||
          normalizeIdentifier(shift.project) ===
            normalizeIdentifier(row.project.name)
        );
      }

      if (row.isObemannad) return Boolean(shift.isObemannad);

      const rowIdentifiers = [
        row.employee?.id,
        row.employee?.uid,
        row.employee?.userId,
        row.employee?.employeeId,
        row.employee?.email,
      ]
        .filter(Boolean)
        .map(normalizeIdentifier);

      const shiftIdentifiers = [
        shift.employeeId,
        ...(shift.employeeIdentifiers || []),
        shift.employeeData?.employeeId,
        shift.employeeData?.uid,
        shift.employeeData?.userId,
        shift.employeeData?.email,
      ]
        .filter(Boolean)
        .map(normalizeIdentifier);

      return rowIdentifiers.some((identifier) => shiftIdentifiers.includes(identifier));
    };

    const getRowMonthStats = (row) => {
      const rowShifts = filteredShifts.filter(
        (shift) => {
          const shiftDate = safeParseDate(shift.date);

          return (
            shiftDate &&
            shiftDate.getFullYear() === currentDate.getFullYear() &&
            shiftDate.getMonth() === currentDate.getMonth() &&
            shiftBelongsToRow(shift, row)
          );
        }
      );

      const hourTotals = rowShifts.reduce((totals, shift) => {
        const { absenceHours } = scheduleHours(shift);
        const hours = calculateShiftHours(shift, shift.employeeData);
        const overtimeHours = shift.approvedUnstaffedPlaceholder
          ? 0 : calculateOvertimeForShift(shift, shift.employeeData).hours;

        return {
          hours: totals.hours + hours,
          overtimeHours: totals.overtimeHours + overtimeHours,
          absenceHours: totals.absenceHours + absenceHours,
        };
      }, { hours: 0, overtimeHours: 0, absenceHours: 0 });

      return {
        shiftCount: rowShifts.length,
        ...hourTotals,
      };
    };

    const renderWeekShift = (shift) => {
      const isObemannad = Boolean(shift.isObemannad);
      const isApprovedUnstaffed = shift.approvedUnstaffedPlaceholder === true;
      const isLocked = isShiftPayrollLocked(shift);
      const isBlocked = isShiftInteractionBlocked(shift);
      const isPublished = isDatePublished(shift.date);
      const isDraggingThis = draggedShift?.id === shift.id;
      const hasReservation = shift.hasReservation === true;

      return (
        <div
          key={shift.id}
          title={!payrollLocksReady ? "Laddar lönestatus" : undefined}
          onClick={(event) => isQuickDeleteMode ? handleQuickDeleteShift(event, shift) : handleEditShiftClick(shift)}
          draggable={!isQuickDeleteMode && !isBlocked}
          onDragStart={(e) => handleDragStart(e, shift)}
          onDragEnd={handleDragEnd}
          style={{
            background: isLocked
              ? "#e8ebf0"
              : isQuickDeleteMode
              ? "#fee2e2"
              : shift.warning
              ? "#fee2e2"
              : isApprovedUnstaffed
              ? "#dcfce7"
              : isObemannad
              ? "#fef3c7"
              : isPublished
              ? "#ffffff"
              : "#f3f4f6",
            border: isLocked
              ? "1px solid #cbd5e1"
              : isQuickDeleteMode
              ? "1px solid #fca5a5"
              : shift.warning
              ? "1px solid #fca5a5"
              : isApprovedUnstaffed
              ? "1px solid #86efac"
              : isObemannad
              ? "1px solid #f59e0b"
              : "1px solid #e5e7eb",
            borderLeft: isLocked
              ? "4px solid #94a3b8"
              : isQuickDeleteMode
              ? "4px solid #dc2626"
              : shift.warning
              ? "4px solid #ef4444"
              : isApprovedUnstaffed
              ? "4px solid #16a34a"
              : isObemannad
              ? "4px solid #f59e0b"
              : `4px solid ${COLORS.blueLight}`,
            borderRadius: 8,
            padding: "8px 28px 8px 9px",
            cursor: isBlocked ? "not-allowed" : isQuickDeleteMode ? "pointer" : "grab",
            position: "relative",
            minWidth: 0,
            boxSizing: "border-box",
            opacity: deletingShiftIds.has(shift.originalId || shift.id) ? 0.35 : isDraggingThis ? 0.5 : 1,
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>
            {shift.startTime && shift.endTime
              ? `${shift.startTime}–${shift.endTime}`
              : shift.time || "Tid saknas"}
          </div>
          <div
            style={{
              marginTop: 3,
              fontSize: 11,
              color: "#6b7280",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {weekRowMode === "projects"
              ? shift.employeeName || shift.employee || "Obemannad"
              : shift.project || "Projekt saknas"}
          </div>
          {(isLocked || shift.hasBookings || shift.hasMessage) && (
            <div style={{ position: "absolute", top: 8, right: 7, display: "flex", gap: 5 }}>
              {isLocked && <PayrollLockInfo />}
              {shift.hasBookings && (
                <FaUsers
                  onClick={(e) => handleBookingsIconClick(e, shift)}
                  style={{ color: hasReservation ? COLORS.orange : COLORS.blue, fontSize: 12, cursor: isBlocked ? "not-allowed" : "pointer" }}
                />
              )}
              {shift.hasMessage && <FaFilePen style={{ color: COLORS.orange, fontSize: 12 }} />}
            </div>
          )}
          {shift.badges?.length > 0 && <BadgeGroup badges={shift.badges} />}
        </div>
      );
    };

    const hasOpenSidePanel = showSheetsPopup || showUnstaffedPanel || showNotificationsPopup;
    const gridColumns = hasOpenSidePanel
      ? "minmax(120px, 160px) repeat(7, minmax(0, 1fr))"
      : "210px repeat(7, minmax(145px, 1fr))";

    return (
      <div style={{ overflowX: hasOpenSidePanel ? "hidden" : "auto", border: "1px solid #e5e7eb", borderRadius: 12 }}>
        <div style={{ minWidth: hasOpenSidePanel ? 0 : 1225, width: "100%", background: "#ffffff" }}>
          <div style={{ display: "grid", gridTemplateColumns: gridColumns }}>
            <div
              style={{
                position: "sticky",
                left: 0,
                zIndex: 3,
                padding: "16px 14px",
                background: "#f9fafb",
                borderRight: "1px solid #e5e7eb",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                gap: 6,
              }}
            >
              {[
                { id: "employees", label: "Användare" },
                { id: "projects", label: "Projekt" },
              ].map((option) => {
                const isActive = weekRowMode === option.id;

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setWeekRowMode(option.id)}
                    style={{
                      flex: 1,
                      padding: "7px 6px",
                      border: isActive
                        ? `1px solid ${COLORS.blue}`
                        : "1px solid #d1d5db",
                      borderRadius: 7,
                      background: isActive ? COLORS.blue : "#ffffff",
                      color: isActive ? "#ffffff" : "#6b7280",
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {weekDays.map((day, index) => {
              const dayNameIndex = (day.getDay() + 6) % 7;
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              const showMonthLabel = day.getDate() === 1;
              const monthLabel = day.toLocaleDateString("sv-SE", {
                month: "short",
              });

              return (
              <div
                key={day.toISOString()}
                style={{
                  padding: hasOpenSidePanel ? "10px 3px" : "12px 8px",
                  textAlign: "center",
                  background: isSameDay(day, new Date()) ? "#eff6ff" : "#f9fafb",
                  borderRight: index < 6 ? "1px solid #e5e7eb" : "none",
                  borderBottom: "1px solid #e5e7eb",
                  borderLeft: showMonthLabel
                    ? `3px solid ${COLORS.blue}`
                    : "none",
                  boxSizing: "border-box",
                }}
              >
                <div style={{ fontSize: hasOpenSidePanel ? 9 : 11, fontWeight: 700, color: isWeekend ? "#ef4444" : "#6b7280", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {hasOpenSidePanel ? dayNames[dayNameIndex].slice(0, 3) : dayNames[dayNameIndex]}
                </div>
                <div style={{ marginTop: 3, fontSize: 13, fontWeight: 600, color: "#111827" }}>
                  {day.getDate()}
                </div>
                <div
                  style={{
                    minHeight: 14,
                    marginTop: 2,
                    fontSize: 10,
                    lineHeight: "14px",
                    fontWeight: 700,
                    color: COLORS.blue,
                    textTransform: "lowercase",
                  }}
                >
                  {showMonthLabel ? monthLabel : ""}
                </div>
              </div>
              );
            })}
          </div>

          {weekRows.map((row) => {
            const rowStats = getRowMonthStats(row);
            const workTime = employeeWorkTime(row.employee,
              workTimeMeasures.companyUid === activeCompany?.companyAccountUid ? workTimeMeasures.byId : {});
            const monthlyTargetHours = monthlyWorkTimeHours(workTime);
            const scheduledPercentage = Number.isFinite(monthlyTargetHours) && monthlyTargetHours > 0 &&
              Number.isFinite(workTime?.percentage) && workTime.percentage > 0
              ? (rowStats.hours + rowStats.overtimeHours) / monthlyTargetHours * workTime.percentage
              : null;

            return (
            <div
              key={row.id}
              id={row.isObemannad ? "unstaffed-week-row" : undefined}
              className={row.isObemannad ? "shift-unstaffed-row" : undefined}
              data-expanded={row.isObemannad ? showUnstaffedRow : undefined}
              inert={row.isObemannad && !showUnstaffedRow ? true : undefined}
              aria-hidden={row.isObemannad && !showUnstaffedRow ? true : undefined}
            >
            <div className={row.isObemannad ? "shift-unstaffed-row-clip" : undefined}>
            <div style={{ display: "grid", gridTemplateColumns: gridColumns }}>
              <div
                style={{
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  minHeight: 112,
                  padding: "14px",
                  background: row.isObemannad ? "#fffbeb" : row.isProject ? "#f8fafc" : "#ffffff",
                  borderRight: "1px solid #e5e7eb",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  flexDirection: "column",
                  gap: 11,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: 9,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: row.isObemannad ? "#92400e" : "#ffffff",
                      background: row.isObemannad
                        ? "#fef3c7"
                        : row.isProject
                        ? `linear-gradient(135deg, ${COLORS.green}, #0ea5a0)`
                        : `linear-gradient(135deg, ${COLORS.blue}, ${COLORS.blueLight})`,
                      fontSize: 13,
                      fontWeight: 800,
                      boxShadow: row.isObemannad
                        ? "none"
                        : "0 3px 8px rgba(37, 99, 235, 0.22)",
                    }}
                  >
                    {row.isObemannad
                      ? "?"
                      : row.isProject
                      ? "P"
                      : row.name.charAt(0).toUpperCase()}
                  </div>
                  <div
                    title={row.name}
                    style={{
                      minWidth: 0,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#252b42",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.name}
                  </div>
                  {row.isObemannad && <UnstaffedInfo />}
                </div>

                <div
                  className="shift-row-stats"
                  style={{
                    width: "100%",
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 4,
                  }}
                >
                {row.employee && (<>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4,
                    padding: "6px 8px", borderRadius: 6, border: "1px solid #bfdbfe", background: "#eff6ff" }}>
                    <span title="Arbetstidsmått" style={{ fontSize: 9, fontWeight: 700, color: "#1e40af" }}>Arbetstidsmått</span>
                    <strong style={{ fontSize: 13, color: COLORS.blue }}>
                      {workTime && Number.isFinite(workTime.percentage)
                        ? `${workTime.percentage.toLocaleString("sv-SE")} %` : "–"}
                    </strong>
                  </div>
                  <div title="Schemalagd procent för vald månad, inklusive övertid. Månadens mål är personens angivna timmar för hela månaden."
                    style={{ display: "flex", flexDirection: "column", gap: 4, padding: "6px 8px",
                      borderRadius: 6, border: "1px solid #bfdbfe", background: "#eff6ff" }}>
                    <span title="Schemalagt" style={{ fontSize: 9, fontWeight: 700, color: "#1e40af" }}>Schemalagt</span>
                    <strong style={{ fontSize: 13, color: "#1e40af" }}>
                      {scheduledPercentage === null ? "–" : `${scheduledPercentage.toLocaleString("sv-SE", { maximumFractionDigits: 1 })} %`}
                    </strong>
                  </div>
                </>)}
                  <div
                    style={{
                      padding: "5px 6px",
                      borderRadius: 6,
                      background: COLORS.blueSoft,
                      border: "1px solid #bfdbfe",
                    }}
                  >
                    <div style={{ fontSize: 13, lineHeight: 1, fontWeight: 800, color: COLORS.blue }}>
                      {rowStats.shiftCount}
                    </div>
                    <div
                      style={{
                        marginTop: 3,
                        fontSize: 9,
                        fontWeight: 700,
                        color: "#1e40af",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      Pass
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "5px 6px",
                      borderRadius: 6,
                      background: "#ecfdf5",
                      border: "1px solid #d1fae5",
                    }}
                  >
                    <div style={{ fontSize: 13, lineHeight: 1, fontWeight: 800, color: "#047857" }}>
                      {rowStats.hours.toLocaleString("sv-SE", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div
                      style={{
                        marginTop: 3,
                        fontSize: 9,
                        fontWeight: 700,
                        color: "#438270",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      Timmar
                    </div>
                  </div>
                  {!row.isObemannad && [
                    { label: "Övertid", hours: rowStats.overtimeHours, background: "#fff7ed", border: "#fed7aa", color: "#9a3412" },
                    { label: "Frånvaro", hours: rowStats.absenceHours, background: "#fef2f2", border: "#fecaca", color: "#991b1b" },
                  ].map((stat) => (
                    <div key={stat.label} style={{ padding: "5px 6px", borderRadius: 6, background: stat.background, border: `1px solid ${stat.border}` }}>
                      <div style={{ fontSize: 13, lineHeight: 1, fontWeight: 800, color: stat.color }}>
                        {stat.hours.toLocaleString("sv-SE", {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                      </div>
                      <div style={{ marginTop: 3, fontSize: 9, fontWeight: 700, color: stat.color, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        {stat.label}
                      </div>
                    </div>
                  ))}
                  {!row.isObemannad && (
                    <div
                      title="Timmar efter rast och frånvaro, inklusive övertid"
                      style={{ gridColumn: "1 / -1", padding: "6px 8px", borderRadius: 6, background: "#eff6ff", border: "1px solid #bfdbfe", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}
                    >
                      <span style={{ fontSize: 9, fontWeight: 700, color: "#1e40af", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        Totalt timmar
                      </span>
                      <strong style={{ fontSize: 13, color: "#1e40af" }}>
                        {(rowStats.hours + rowStats.overtimeHours).toLocaleString("sv-SE", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </strong>
                    </div>
                  )}
                </div>
              </div>

              {weekDays.map((day, dayIndex) => {
                const cellShifts = filteredShifts.filter(
                  (shift) => isSameDay(shift.date, day) && shiftBelongsToRow(shift, row)
                );
                const addShiftLocked = Boolean(row.employee && isEmployeePayrollLocked(row.employee.id, day));
                const addShiftBlocked = !payrollLocksReady || addShiftLocked;
                const isDragOver =
                  dragOverDate &&
                  isSameDay(dragOverDate, day) &&
                  (!dragOverRowId || dragOverRowId === row.id);

                return (
                  <div
                    key={`${row.id}-${day.toISOString()}`}
                    onDragOver={(e) => handleDragOver(e, day, row)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, day, row)}
                    style={{
                      minHeight: 112,
                      padding: 6,
                      display: "flex",
                      flexDirection: "column",
                      gap: 5,
                      background: isDragOver ? "#eff6ff" : isSameDay(day, new Date()) ? "#fafcff" : "#ffffff",
                      outline: isDragOver ? "2px dashed #3b82f6" : "none",
                      outlineOffset: -3,
                      borderRight: dayIndex < 6 ? "1px solid #e5e7eb" : "none",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {cellShifts.map(renderWeekShift)}
                    <button
                      type="button"
                      disabled={addShiftBlocked}
                      title={addShiftLocked ? "Låst: avmarkera Godkänd för personen i Löneexport för att skapa pass" : undefined}
                      onClick={() =>
                        handleAddShiftClick(
                          day,
                          row.employee,
                          row.isObemannad,
                          row.project
                        )
                      }
                      aria-label={addShiftLocked ? `Låst: kan inte lägga till pass för ${row.name}` : `Lägg till pass för ${row.name}`}
                      style={{
                        marginTop: "auto",
                        minHeight: 28,
                        border: "1px dashed #d1d5db",
                        borderRadius: 7,
                        background: "transparent",
                        color: "#9ca3af",
                        cursor: addShiftBlocked ? "not-allowed" : "pointer",
                        opacity: addShiftBlocked ? 0.5 : 1,
                        fontSize: 16,
                      }}
                    >
                      {addShiftLocked ? <FaLock size={12} aria-hidden="true" /> : "+"}
                    </button>
                  </div>
                );
              })}
            </div>
            </div>
            </div>
            );
          })}

          {weekRows.length === 0 && (
            <div style={{ padding: 36, textAlign: "center", color: "#6b7280" }}>
              {weekRowMode === "projects"
                ? "Inga projekt hittades."
                : "Inga användare hittades."}
            </div>
          )}
        </div>
      </div>
    );
  };
 
  const renderDayColumns = () => (
    <div className="shift-day-scroll" aria-label="Schema måndag till söndag">
      <div className="shift-day-columns">
        {weekDays.map((day) => {
          const today = isSameDay(day, new Date());
          const dayShifts = filteredShifts
            .filter((shift) => isSameDay(shift.date, day))
            .sort((a, b) => String(a.startTime || a.time || "").localeCompare(String(b.startTime || b.time || "")));
          return (
            <section key={day.toDateString()} className={`shift-day-column${today ? " is-today" : ""}${day.getDate() === 1 ? " is-month-start" : ""}`}>
              <header className="shift-day-header">
                <div>
                  <h3 style={day.getDay() === 0 || day.getDay() === 6 ? { color: "#ef4444" } : undefined}>{day.toLocaleDateString("sv-SE", { weekday: "long" })}</h3>
                  <div className="shift-day-date"><strong>{day.getDate()}</strong> {day.toLocaleDateString("sv-SE", { month: "long" })}</div>
                </div>
                <button type="button" className="shift-day-add" aria-label={`Skapa pass ${day.toLocaleDateString("sv-SE")}`} onClick={() => handleAddShiftClick(day)}><FaPlus aria-hidden="true" /></button>
              </header>
              <div className="shift-day-cards">
                {dayShifts.map((shift) => {
                  const name = shift.employeeName || shift.employee || "Obemannad";
                  const initials = name.trim().split(/\s+/).map((part) => part[0]).slice(0, 2).join("").toUpperCase();
                  const locked = isShiftPayrollLocked(shift);
                  const blocked = isShiftInteractionBlocked(shift);
                  return (
                    <div key={shift.id} className={`shift-day-card${!isDatePublished(shift.date) ? " is-unpublished" : ""}${shift.isObemannad ? " is-unstaffed" : ""}${locked ? " is-locked" : ""}${isQuickDeleteMode ? " is-deleting" : ""}`} style={{ opacity: deletingShiftIds.has(shift.originalId || shift.id) ? 0.35 : 1 }}>
                      <button type="button" className="shift-day-card-open" disabled={blocked} onClick={(event) => isQuickDeleteMode ? handleQuickDeleteShift(event, shift) : handleEditShiftClick(shift)} aria-label={`${shift.startTime && shift.endTime ? `${shift.startTime}–${shift.endTime}` : shift.time || "Tid saknas"}, ${name}, ${shift.project || "Projekt saknas"}`}>
                        <strong className="shift-day-time">{shift.startTime && shift.endTime ? `${shift.startTime} - ${shift.endTime}` : shift.time || "Tid saknas"}</strong>
                        <FaEllipsisV className="shift-day-more" aria-hidden="true" />
                        <span className="shift-day-person">
                          <span className="shift-day-avatar" aria-hidden="true">{initials}</span>
                          <span className="shift-day-person-details"><strong title={name}>{name}</strong><span className="shift-day-project"><i aria-hidden="true" /><span title={shift.project || "Projekt saknas"}>{shift.project || "Projekt saknas"}</span></span></span>
                        </span>
                      </button>
                      {locked && <PayrollLockInfo />}
                      {shift.badges?.length > 0 && <BadgeGroup badges={shift.badges} />}
                    </div>
                  );
                })}
                {!dayShifts.length && <p className="shift-day-empty">Inga pass</p>}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );

  const renderMonth = () => {
    const dayNames = [
      "MÅNDAG",
      "TISDAG",
      "ONSDAG",
      "TORSDAG",
      "FREDAG",
      "LÖRDAG",
      "SÖNDAG",
    ];
    const currentMonth = currentDate.getMonth();
 
    const weeks = [];
    for (let i = 0; i < monthDays.length; i += 7) {
      weeks.push(monthDays.slice(i, i + 7));
    }

    const monthSummaryShifts = filteredShifts.filter((shift) => {
      if (shift.approvedUnstaffedPlaceholder) return false;
      const shiftDate = safeParseDate(shift.date);
      return shiftDate
        && shiftDate.getFullYear() === currentDate.getFullYear()
        && shiftDate.getMonth() === currentMonth;
    });
    const monthTotalHours = monthSummaryShifts.reduce(
      (total, shift) => total + calculateShiftHours(shift, shift.employeeData), 0
    );
 
    return (
      <div style={{ background: "#ffffff", borderRadius: 12, overflow: "hidden" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            background: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          {dayNames.map((day, index) => (
            <div
              key={index}
              style={{
                padding: "16px 8px",
                textAlign: "center",
                fontSize: 14,
                fontWeight: 600,
                color: index === 5 || index === 6 ? "#ef4444" : "#374151",
              }}
            >
              {day}
            </div>
          ))}
        </div>
 
        <div style={{ display: "flex", flexDirection: "column" }}>
          {weeks.map((week, weekIndex) => (
            <div
              key={weekIndex}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                borderBottom:
                  weekIndex < weeks.length - 1 ? "1px solid #e5e7eb" : "none",
              }}
            >
              {week.map((day, dayIndex) => {
                if (!day) return null;
 
                const currentDay = new Date(day);
                const isCurrentMonth = currentDay.getMonth() === currentMonth;
                const isToday = isSameDay(currentDay, new Date());
                const dayNumber = currentDay.getDate();
                const isDragOver = dragOverDate && isSameDay(dragOverDate, currentDay);
 
                const dayShifts = filteredShifts.filter((shift) => {
                  if (!shift.date || shift.approvedUnstaffedPlaceholder) return false;
                  return isSameDay(shift.date, currentDay);
                });
 
                const hasShifts = dayShifts.length > 0;
                const addShiftLocked = Boolean(selectedUserId && isEmployeePayrollLocked(selectedUserId, currentDay));
                const addShiftBlocked = !payrollLocksReady || addShiftLocked;
 
                return (
                  <div
                    key={dayIndex}
                    onDragOver={(e) => handleDragOver(e, currentDay)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, currentDay)}
                    style={{
                      minHeight: 180,
                      padding: "12px 8px",
                      borderRight: dayIndex < 6 ? "1px solid #e5e7eb" : "none",
                      background: isCurrentMonth ? "#ffffff" : "#f9fafb",
                      opacity: isCurrentMonth ? 1 : 0.5,
                      position: "relative",
                      ...(isDragOver && {
                        background: "#e0f2fe",
                        boxShadow: "inset 0 0 0 2px #3b82f6",
                      }),
                    }}
                  >
                    <button
                      type="button"
                      disabled={addShiftBlocked}
                      title={addShiftLocked ? "Låst: avmarkera Godkänd för personen i Löneexport för att skapa pass" : undefined}
                      aria-label={addShiftLocked ? "Låst: kan inte lägga till pass för godkänd person" : "Lägg till pass"}
                      onClick={(e) => {
                        e.stopPropagation();
                        const selectedDate = new Date(currentDay);
                        handleAddShiftClick(selectedDate);
                      }}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        border: "1px solid #cbd5e1",
                        background: "#ffffff",
                        color: "#64748b",
                        fontSize: 16,
                        fontWeight: "bold",
                        cursor: addShiftBlocked ? "not-allowed" : "pointer",
                        opacity: addShiftBlocked ? 0.5 : 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s",
                        zIndex: 10,
                      }}
                      onMouseEnter={(e) => {
                        if (addShiftBlocked) return;
                        e.currentTarget.style.background = "#3b82f6";
                        e.currentTarget.style.color = "#ffffff";
                        e.currentTarget.style.borderColor = "#3b82f6";
                      }}
                      onMouseLeave={(e) => {
                        if (addShiftBlocked) return;
                        e.currentTarget.style.background = "#ffffff";
                        e.currentTarget.style.color = "#64748b";
                        e.currentTarget.style.borderColor = "#cbd5e1";
                      }}
                    >
                      {addShiftLocked ? <FaLock size={12} aria-hidden="true" /> : "+"}
                    </button>
 
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          background: isToday ? "#3b82f6" : "transparent",
                          color: isToday
                            ? "#ffffff"
                            : isCurrentMonth
                            ? "#111827"
                            : "#9ca3af",
                          fontWeight: isToday ? 600 : 400,
                          fontSize: 14,
                        }}
                      >
                        {dayNumber}
                      </span>
                    </div>
 
                    {hasShifts && (
                      <div style={{ marginTop: 8 }}>
                        {dayShifts.map((shift) => {
                          const employeeName = shift.employeeName || shift.employee || "Okänd";
                          const isObemannad = !!shift.isObemannad;
                          const hasBadges = shift.badges && shift.badges.length > 0;
                          const isPublished = isDatePublished(shift.date);
                          const isDraggingThis = draggedShift?.id === shift.id;
                          const hasMessage = shift.hasMessage === true;
                          const hasBookings = shift.hasBookings === true;
                          const isBooked = shift.bookedFromApplications === true;
                          const isLocked = isShiftPayrollLocked(shift);
                          const isBlocked = isShiftInteractionBlocked(shift);
                          const hasReservation = shift.hasReservation === true;
 
                          const badgeIndicator = hasBadges ? " •" : "";
                          const projectName = shift.project || "Projekt saknas";
                          const timeString =
                            shift.startTime && shift.endTime
                              ? `${shift.startTime}-${shift.endTime}`
                              : shift.time || "Tid saknas";
 
                          return (
                            <div
                              key={shift.id}
                              title={!payrollLocksReady ? "Laddar lönestatus" : undefined}
                              onClick={(event) => isQuickDeleteMode ? handleQuickDeleteShift(event, shift) : handleEditShiftClick(shift)}
                              draggable={!isQuickDeleteMode && !isBlocked}
                              onDragStart={(e) => handleDragStart(e, shift)}
                              onDragEnd={handleDragEnd}
                              style={{
                                fontSize: 11,
                                padding: "6px 8px",
                                marginBottom: 4,
                                background: isLocked
                                  ? "#e8ebf0"
                                  : isQuickDeleteMode
                                  ? "#fee2e2"
                                  : shift.warning
                                  ? "#fee2e2"
                                  : isObemannad
                                  ? "#fef3c7"
                                  : isBooked
                                  ? isPublished
                                    ? "transparent"
                                    : "#f3f4f6"
                                  : isPublished
                                  ? "transparent"
                                  : "#f3f4f6",
                                border: isLocked
                                  ? "1px solid #cbd5e1"
                                  : isQuickDeleteMode
                                  ? "1px solid #fca5a5"
                                  : isObemannad
                                  ? "1px solid #fcd34d"
                                  : shift.warning
                                  ? "1px solid #fca5a5"
                                  : "1px solid #e5e7eb",
                                borderRadius: 6,
                                cursor: isBlocked ? "not-allowed" : isQuickDeleteMode ? "pointer" : "grab",
                                transition: "all 0.2s, opacity 0.2s",
                                opacity: deletingShiftIds.has(shift.originalId || shift.id) ? 0.35 : isDraggingThis ? 0.5 : 1,
                                position: "relative",
                              }}
                              onMouseEnter={(e) => {
                                if (isBlocked) return;
                                e.currentTarget.style.background = shift.warning
                                  ? "#fecaca"
                                  : isObemannad
                                  ? "#fde68a"
                                  : isBooked
                                  ? isPublished
                                    ? "#f3f4f6"
                                    : "#e5e7eb"
                                  : isPublished
                                  ? "#f3f4f6"
                                  : "#e5e7eb";
                                e.currentTarget.style.transform = "translateX(2px)";
                              }}
                              onMouseLeave={(e) => {
                                if (isBlocked) return;
                                e.currentTarget.style.background = shift.warning
                                  ? "#fee2e2"
                                  : isObemannad
                                  ? "#fef3c7"
                                  : isBooked
                                  ? isPublished
                                    ? "transparent"
                                    : "#f3f4f6"
                                  : isPublished
                                  ? "transparent"
                                  : "#f3f4f6";
                                e.currentTarget.style.transform = "translateX(0)";
                              }}
                            >
                              <div style={{ 
                                fontWeight: 600, 
                                marginBottom: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between"
                              }}>
                                <span>{employeeName}</span>
                                {badgeIndicator}
                              </div>
 
                              <div style={{ 
                                fontSize: 10, 
                                color: "#6b7280", 
                                marginBottom: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: "4px"
                              }}>
                                <span>{timeString}</span>
                              </div>
 
                              <div style={{ fontSize: 10, color: "#6b7280", fontStyle: "italic" }}>
                                {projectName.length > 20
                                  ? projectName.substring(0, 20) + "..."
                                  : projectName}
                              </div>
 
                              <div
                                style={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  display: "flex",
                                  gap: 4,
                                }}
                              >
                                {isLocked && <PayrollLockInfo small />}
                                {hasMessage && (
                                  <FaFilePen
                                    style={{
                                      fontSize: "10px",
                                      color: COLORS.orange,
                                    }}
                                  />
                                )}
 
                                {hasBookings && (
                                  <FaUsers
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleBookingsIconClick(e, shift);
                                    }}
                                    style={{
                                      fontSize: "10px",
                                      color: hasReservation ? COLORS.orange : COLORS.blue,
                                      cursor: "pointer",
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            padding: "18px 20px",
            borderTop: "1px solid #e5e7eb",
            background: "linear-gradient(135deg, #fafaff 0%, #f8fafc 100%)",
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#374151" }}>Månadssammanfattning</div>
            <div style={{ marginTop: 3, fontSize: 10, color: "#94a3b8" }}>Visar resultatet för dina valda filter</div>
          </div>
          <div style={{ display: "flex", alignItems: "stretch", gap: 10 }}>
            <div style={{ minWidth: 112, padding: "11px 14px", border: "1px solid #bfdbfe", borderRadius: 10, background: "#eff6ff", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: "#2563eb", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}><FaCalendarPlus /></div>
              <div><strong style={{ display: "block", fontSize: 18, lineHeight: 1, color: "#1e40af" }}>{monthSummaryShifts.length}</strong><span style={{ display: "block", marginTop: 4, fontSize: 9, fontWeight: 800, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: ".05em" }}>Pass</span></div>
            </div>
            <div style={{ minWidth: 112, padding: "11px 14px", border: "1px solid #bbf7d0", borderRadius: 10, background: "#ecfdf5", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: "#059669", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}><FaClock /></div>
              <div><strong style={{ display: "block", fontSize: 18, lineHeight: 1, color: "#047857" }}>{monthTotalHours.toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong><span style={{ display: "block", marginTop: 4, fontSize: 9, fontWeight: 800, color: "#059669", textTransform: "uppercase", letterSpacing: ".05em" }}>Timmar</span></div>
            </div>
          </div>
        </div>
      </div>
    );
  };
 
  const formatDisplayDate = () => {
    if (!currentDate) return "";
 
    try {
      if (viewMode === "week") {
        const weekNumber = getWeekNumber(weekDays[0] || currentDate);
        const month = currentDate.toLocaleString("sv-SE", { month: "long" });
        return `Vecka ${weekNumber}, ${
          month.charAt(0).toUpperCase() + month.slice(1)
        } ${currentDate.getFullYear()}`;
      } else {
        const month = currentDate.toLocaleString("sv-SE", { month: "long" });
        return `${month.charAt(0).toUpperCase() + month.slice(1)} ${currentDate.getFullYear()}`;
      }
    } catch (error) {
      console.error("Fel vid formatering av datum:", error);
      return "";
    }
  };
 
  const renderDatePicker = () => {
    if (!showDatePicker) return null;
 
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push(i);
    }
 
    const months = [
      "Januari",
      "Februari",
      "Mars",
      "April",
      "Maj",
      "Juni",
      "Juli",
      "Augusti",
      "September",
      "Oktober",
      "November",
      "December",
    ];
 
    return (
      <div
        ref={datePickerRef}
        style={{
          position: "fixed",
          top: dateDisplayRef.current
            ? dateDisplayRef.current.getBoundingClientRect().bottom + window.scrollY + 4
            : "50%",
          left: dateDisplayRef.current
            ? dateDisplayRef.current.getBoundingClientRect().left + window.scrollX
            : "50%",
          transform: dateDisplayRef.current ? "none" : "translate(-50%, -50%)",
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          boxShadow:
            "0 20px 25px -5px rgba(0,0,0,0.2), 0 10px 10px -5px rgba(0,0,0,0.1)",
          zIndex: 9999,
          width: 320,
          padding: 16,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <select
            value={tempSelectedDate.getFullYear()}
            onChange={(e) => {
              const newDate = new Date(tempSelectedDate);
              newDate.setFullYear(parseInt(e.target.value, 10));
              setTempSelectedDate(newDate);
            }}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              fontSize: 14,
              marginBottom: 8,
            }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
 
          <select
            value={tempSelectedDate.getMonth()}
            onChange={(e) => {
              const newDate = new Date(tempSelectedDate);
              newDate.setMonth(parseInt(e.target.value, 10));
              setTempSelectedDate(newDate);
            }}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              fontSize: 14,
            }}
          >
            {months.map((month, index) => (
              <option key={month} value={index}>
                {month}
              </option>
            ))}
          </select>
        </div>
 
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 2,
            marginBottom: 12,
          }}
        >
          {["M", "T", "O", "T", "F", "L", "S"].map((day, index) => (
            <div
              key={index}
              style={{
                textAlign: "center",
                fontSize: 12,
                fontWeight: 600,
                color: index === 5 || index === 6 ? "#ef4444" : "#6b7280",
                padding: "4px 0",
              }}
            >
              {day}
            </div>
          ))}
        </div>
 
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 2,
          }}
        >
          {getMonthDays(tempSelectedDate).map((date, index) => {
            if (!date) return <div key={index} />;
 
            const isCurrentMonth = date.getMonth() === tempSelectedDate.getMonth();
            const isSelected = isSameDay(date, currentDate);
            const isToday = isSameDay(date, new Date());
 
            return (
              <button
                key={index}
                onClick={() => handleDateSelect(date)}
                style={{
                  aspectRatio: "1",
                  border: "none",
                  background: isSelected
                    ? "#3b82f6"
                    : isToday
                    ? "#eff6ff"
                    : "transparent",
                  color: isSelected
                    ? "#ffffff"
                    : isCurrentMonth
                    ? "#111827"
                    : "#9ca3af",
                  fontWeight: isSelected || isToday ? 600 : 400,
                  fontSize: 13,
                  borderRadius: 4,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = isToday ? "#eff6ff" : "transparent";
                  }
                }}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
 
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 12,
            borderTop: "1px solid #e5e7eb",
            paddingTop: 12,
          }}
        >
          <button
            onClick={() => handleDateSelect(new Date())}
            style={{
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              background: "#ffffff",
              borderRadius: 6,
              fontSize: 13,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Idag
          </button>
          <button
            onClick={() => setShowDatePicker(false)}
            style={{
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              background: "#3b82f6",
              color: "#ffffff",
              borderRadius: 6,
              fontSize: 13,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Stäng
          </button>
        </div>
      </div>
    );
  };
 
  const companyUid = activeCompany?.companyAccountUid;
  const scheduleIsLoading = companyUid && (
    permissionsLoading ||
    schemasReadyCompanyUid !== companyUid ||
    employeesReadyCompanyUid !== companyUid ||
    (schemaOptions.length > 0 && !selectedSchemaDetails)
  );

  if (companyLoading || (loading && !employees.length) || (scheduleIsLoading && scheduleLoadErrorCompanyUid !== companyUid)) {
    return <LoadingIndicator message="Laddar schemadata..." fullPage />;
  }

  if (scheduleLoadErrorCompanyUid === companyUid && activeCompany) {
    return <div role="alert" style={{ padding: 24 }}>Kunde inte hämta schemadata. Försök ladda om sidan.</div>;
  }
 
  if (!activeCompany) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <p>Inget företag valt. Vänligen logga in igen.</p>
        <button
          onClick={() => (window.location.href = "/login")}
          style={{
            padding: "10px 20px",
            background: COLORS.blue,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          Gå till inloggning
        </button>
      </div>
    );
  }
 
  const selectedUserFullName = selectedUserId
    ? employees.find((e) => e.id === selectedUserId)?.name || selectedUser
    : "";
 
  const isUsingCache = shiftPageCache.schemasLoaded && 
                       shiftPageCache.schemasCompanyUid === activeCompany?.companyAccountUid;
 
  return (
    <div style={{ 
      padding: "0 0 24px",
      marginRight: showSheetsPopup || showUnstaffedPanel || showNotificationsPopup ? "400px" : "0",
      transition: 'margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      maxWidth: showSheetsPopup || showUnstaffedPanel || showNotificationsPopup ? "calc(100% - 400px)" : "100%",
      boxSizing: "border-box",
      minWidth: 0,
    }}>
      <div style={{ marginBottom: 16, padding: "0 0 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Visar schema för: <strong>{activeCompany.companyName}</strong>
          {isUsingCache && (
            <span style={{ marginLeft: 12, fontSize: 12, color: "#22c55e" }}>
 
            </span>
          )}
        </p>
      </div>
 
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 0 20px",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          flexWrap: "nowrap",
          position: "relative",
          zIndex: 100,
        }}
      >
        <div style={{ ...boxStyle, minWidth: 170 }}>
          <select
            value={selectedSchema}
            onChange={(e) => setSelectedSchema(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
            disabled={!schemaOptions.length}
          >
            {!schemaOptions.length ? (
              <option value="">Inga scheman</option>
            ) : (
              schemaOptions.map((schema) => (
                <option key={schema.id} value={schema.id}>
                  {schema.name}
                </option>
              ))
            )}
          </select>
 
          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
 
          <span style={labelStyle}>Schema</span>
        </div>
 
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => changeWeek(-1)}
            style={{
              cursor: "pointer",
              border: "1px solid #d1d5db",
              borderRadius: 3,
              background: "#ffffff",
              fontSize: 20,
              color: "#111827",
              width: 46,
              height: 50,
            }}
          >
            ←
          </button>
 
          <div
            ref={dateDisplayRef}
            onClick={() => setShowDatePicker(!showDatePicker)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 260,
              height: 50,
              padding: "0 16px",
              border: "1px solid #d1d5db",
              borderRadius: 3,
              background: "#ffffff",
              fontSize: 15,
              color: "#111827",
              fontWeight: 500,
              boxSizing: "border-box",
              cursor: "pointer",
              transition: "background 0.2s, border-color 0.2s",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f9fafb";
              e.currentTarget.style.borderColor = "#9ca3af";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#ffffff";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
          >
            {formatDisplayDate()}
          </div>
 
          <button
            onClick={() => changeWeek(1)}
            style={{
              cursor: "pointer",
              border: "1px solid #d1d5db",
              borderRadius: 3,
              background: "#ffffff",
              fontSize: 20,
              color: "#111827",
              width: 46,
              height: 50,
            }}
          >
            →
          </button>
        </div>
 
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            height: 50,
            width: 150,
            paddingLeft: 12,
            paddingRight: 12,
            borderRadius: 6,
            border: "1px solid #e5e7eb",
            background: "#ffffff",
            fontSize: 13,
            flexShrink: 0,
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "#111827",
              whiteSpace: "nowrap",
            }}
          >
            {viewMode === "week" ? (showDayColumns ? "Dagvy" : "Veckovy") : "Månadsvy"}
          </span>
 
          <button
            type="button"
            onClick={() => {
              setViewMode(viewMode === "week" ? "month" : "week");
            }}
            style={{
              position: "relative",
              width: 36,
              height: 20,
              borderRadius: 999,
              border: "1px solid #d1d5db",
              padding: 0,
              cursor: "pointer",
              background: "#f9fafb",
            }}
          >
            <span
              style={{
                position: "absolute",
                top: 1,
                left: viewMode === "week" ? 2 : 18,
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: "#e5e7eb",
                boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                transition: "left 140ms ease-out",
              }}
            />
          </button>
        </div>
 
        <div style={boxStyle}>
          <input
            list="userListSelect"
            value={selectedUser}
            onChange={handleUserSelect}
            placeholder="Välj användare"
            style={inputStyle}
          />
          <datalist id="userListSelect">
            {visibleEmployees.map((emp) => (
              <option key={emp.id} value={emp.name}>
                {emp.name}
              </option>
            ))}
          </datalist>
          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 11,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Användare</span>
        </div>
 
        <div style={boxStyle}>
          <input
            list="projectList"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            placeholder="Projekt"
            style={inputStyle}
          />
          <datalist id="projectList">
            {projects.map((project) => (
              <option key={project.id} value={project.name} />
            ))}
          </datalist>
          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 11,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Projekt</span>
        </div>
 
        <div style={{ ...boxStyle, minWidth: 180 }}>
          <span style={labelStyle}>Publicerat fram t.o.m</span>
          <button
            onClick={() => setShowPublishPopup(true)}
            style={{
              width: "100%",
              height: 50,
              padding: "8px 12px",
              borderRadius: 3,
              border: "1px solid #d1d5db",
              background: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
              cursor: "pointer",
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#111827",
              }}
            >
              {publishedUntil ? publishedUntil.toLocaleDateString("sv-SE") : "Välj datum"}
            </span>
          </button>
        </div>

        <div
          role="toolbar"
          aria-label="Schemaåtgärder"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            flexShrink: 0,
          }}
        >
          {viewMode === "week" && weekRowMode === "employees" && (
            <button
              type="button"
              className="shift-unstaffed-toggle"
              onClick={() => setShowUnstaffedRow((visible) => !visible)}
              aria-expanded={showUnstaffedRow}
              aria-controls="unstaffed-week-row"
              aria-label={showUnstaffedRow ? "Dölj raden Obemannad" : "Visa raden Obemannad"}
              title={showUnstaffedRow ? "Dölj raden Obemannad" : "Visa raden Obemannad"}
              style={{
                ...toolbarButtonStyle,
                gap: 3,
                background: showUnstaffedRow ? "#fffbeb" : "#ffffff",
                color: "#b45309",
                border: "1px solid #fcd34d",
              }}
            >
              <FaUserSlash aria-hidden="true" />
              <FaChevronDown
                aria-hidden="true"
                className="shift-unstaffed-chevron"
                style={{ fontSize: 10, transform: showUnstaffedRow ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </button>
          )}

          <button
            type="button"
            aria-label={showDayColumns && viewMode === "week" ? "Visa schemats radvy" : "Visa schemats dagvy"}
            title={showDayColumns && viewMode === "week" ? "Visa radvy" : "Visa dagvy"}
            aria-pressed={showDayColumns && viewMode === "week"}
            onClick={() => {
              setShowDayColumns(viewMode !== "week" || !showDayColumns);
              setViewMode("week");
            }}
            style={{ ...toolbarButtonStyle, background: showDayColumns && viewMode === "week" ? "#2563eb" : "#ffffff", color: showDayColumns && viewMode === "week" ? "#ffffff" : "#2563eb", border: "1px solid #2563eb", borderRadius: "50%" }}
          >
            <FaCalendarAlt aria-hidden="true" />
          </button>

          <button
            type="button"
            aria-label="Skapa pass"
            title="Skapa pass"
            onClick={handleTopAddShiftClick}
            style={{
              ...toolbarButtonStyle,
              background: "#3b82f6",
              border: "1px solid #2563eb",
            }}
          >
            <FaPlus />
          </button>


          <button
            type="button"
            aria-label={notifications.unreadCount ? `Notifikationer, ${notifications.unreadCount} olästa` : "Notifikationer"}
            title="Notifikationer"
            aria-expanded={showNotificationsPopup}
            onClick={() => {
              setShowSheetsPopup(false);
              setShowUnstaffedPanel(false);
              setShowNotificationsPopup((open) => !open);
            }}
            style={{ ...toolbarButtonStyle, position: "relative", background: "#3b82f6", border: "1px solid #1d4ed8" }}
          >
            <FaBell />
            {notifications.unreadCount > 0 && <span className="schedule-notifications-badge" aria-hidden="true">{notifications.unreadCount > 9 ? "9+" : notifications.unreadCount}</span>}
          </button>


          <button
            onClick={() => setShowSchedulingPopup(true)}
            aria-label={unassignedEmployeeCount ? `Öppna scheman, ${unassignedEmployeeCount} personer utan schema` : "Öppna scheman"}
            title={unassignedEmployeeCount ? `Hantera scheman och personer – ${unassignedEmployeeCount} personer utan schema` : "Hantera scheman och personer"}
            style={{
              ...toolbarButtonStyle,
              position: "relative",
              background: "#6366f1",
              border: "1px solid #4f46e5",
            }}
          >
            <FaCalendarPlus />
            {unassignedEmployeeCount > 0 && <span className="schedule-notifications-badge" aria-hidden="true">{unassignedEmployeeCount > 9 ? "9+" : unassignedEmployeeCount}</span>}
          </button>


 

 
          <button
            onClick={handleSheetsClick}
            aria-label="Öppna AI-schemaläggaren"
            title="AI-schemaläggare"
            style={{
              ...toolbarButtonStyle,
              background: "#10b981",
              border: "1px solid #059669",
            }}
          >
            <FaMagic />
          </button>




          <button
            onClick={handleUnstaffedClick}
            aria-label="Visa obemannade pass"
            title="Obemannade pass"
            style={{
              ...toolbarButtonStyle,
              background: "#f59e0b",
              border: "1px solid #d97706",
            }}
          >
            <FaUserSlash />
          </button>

          <button
            type="button"
            onClick={() => setIsQuickDeleteMode((current) => !current)}
            aria-pressed={isQuickDeleteMode}
            aria-label={isQuickDeleteMode ? "Avsluta raderingsläget" : "Aktivera raderingsläget"}
            title={isQuickDeleteMode ? "Avsluta raderingsläget" : "Radera flera pass snabbt"}
            style={{
              ...toolbarButtonStyle,
              background: isQuickDeleteMode ? "#b91c1c" : "#dc2626",
              border: isQuickDeleteMode ? "2px solid #7f1d1d" : "1px solid #b91c1c",
              fontSize: 12,
              fontWeight: 800,
              boxShadow: isQuickDeleteMode ? "0 0 0 3px rgba(220,38,38,.2)" : "none",
            }}
          >
            <FaTrash />
          </button>
        </div>
      </div>
 
      {viewMode === "week" ? (
        showDayColumns ? renderDayColumns() : renderWeek()
      ) : !selectedUserId ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 400,
            background: "#f9fafb",
            borderRadius: 12,
            border: "1px dashed #cbd5e1",
          }}
        >
          <div style={{ textAlign: "center", padding: "48px 24px" }}>
            <FaUsers style={{ fontSize: 48, color: "#9ca3af", marginBottom: 16 }} />
            <h3 style={{ fontSize: 18, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              Välj en användare
            </h3>
            <p style={{ fontSize: 14, color: "#6b7280" }}>
              För att visa månadsvy behöver du först välja en användare i filtret ovan.
            </p>
          </div>
        </div>
      ) : (
        renderMonth()
      )}
 
      {renderDatePicker()}
 
      <PopupShift
        key={
          selectedShift?.id ||
          (selectedPopupDate ? `${selectedPopupDate.getFullYear()}-${selectedPopupDate.getMonth()}-${selectedPopupDate.getDate()}` : "new")
        }
        isOpen={showCreatePopup}
        onClose={closeShiftPopup}
        selectedDate={selectedPopupDate}
        selectedShift={shifts.find((shift) => shift.id === selectedShift?.id) || selectedShift}
        selectedSchema={selectedSchema}
        companyRootUid={activeCompany?.companyAccountUid}
        payrollLocks={payrollLocks}
        isDatePublished={isDatePublished}
        companyId={activeCompany?.companyId}
        hideEmployeeDetails={viewMode === "month" && !selectedShift}
        preselectedEmployeeId={
          popupPreselectedObemannad
            ? ""
            : popupPreselectedEmployee?.id || selectedUserId
        }
        preselectedEmployeeName={
          popupPreselectedObemannad
            ? ""
            : popupPreselectedEmployee?.name ||
              popupPreselectedEmployee?.userName ||
              selectedUserFullName
        }
        preselectedObemannad={popupPreselectedObemannad}
        preselectedProject={popupPreselectedProject}
      />
 
      {notifications.toast && <div className="schedule-notifications-toast" role="status">
        <FaBell aria-hidden="true" />
        <button type="button" className="schedule-notifications-toast-open" onClick={() => {
          setShowSheetsPopup(false);
          setShowUnstaffedPanel(false);
          setShowNotificationsPopup(true);
        }}>
          <strong>{notifications.toast.name}: {notifications.toast.title}</strong>
          <span>{notifications.toast.text}</span>
          {notifications.unreadCount > 1 && <small>{notifications.unreadCount} olästa notiser – visa alla</small>}
        </button>
        <button type="button" className="schedule-notifications-toast-close" onClick={notifications.dismissToast} aria-label="Stäng notis"><FaTimes /></button>
      </div>}
      {showNotificationsPopup && <NotificationsPopup
        key={notifications.settingsKey}
        onClose={() => setShowNotificationsPopup(false)}
        settings={notifications.settings}
        messages={notifications.messages}
        onSave={notifications.saveSettings}
        onClearAll={notifications.clearAll}
        onClearMessage={notifications.clearMessage}
        error={notifications.error}
        currentDate={currentDate}
      />}

      <PublishPopup
        isOpen={showPublishPopup}
        onClose={() => setShowPublishPopup(false)}
        onPublish={handlePublish}
        currentPublishedUntil={publishedUntil}
        companyRootUid={activeCompany?.companyAccountUid}
      />
 
      {activeCompany?.companyAccountUid && <ConnectedSchedulingPopup
        key={activeCompany.companyAccountUid}
        isOpen={showSchedulingPopup}
        onUnassignedCountChange={setUnassignedEmployeeCount}
        onClose={() => setShowSchedulingPopup(false)}
        companyUid={activeCompany?.companyAccountUid}
        role={activeCompany?.role}
        allowedSchemaIds={allowedSchemaIds}
        permissionsLoading={permissionsLoading}
        onSchemaCreated={(schemaId, name) => {
          setSchemas((previous) => previous.some((schema) => schema.id === schemaId)
            ? previous
            : [...previous, { id: schemaId, name, visibleEmployeeIds: [] }]
              .sort((a, b) => a.name.localeCompare(b.name, "sv")));
          setSelectedSchema(schemaId);
        }}
      />}
 
      <CreateAutomaticShiftPopup
        initialEmployeeId={selectedUserId}
        onSelectTemplate={setSelectedTemplate}
        selectedTemplate={selectedTemplate}
        isOpen={showSheetsPopup}
        onClose={() => setShowSheetsPopup(false)}
        companyRootUid={activeCompany?.companyAccountUid}
        selectedMonth={currentDate}
        selectedSchema={selectedSchema}
      />

      {showUnstaffedPanel && (
        <aside
          aria-label="Obemannade pass"
          style={{
            position: "fixed", top: 0, right: 0, width: 420, maxWidth: "100vw",
            height: "100vh", zIndex: 9999, display: "flex", flexDirection: "column",
            background: "#ffffff", color: "#0f172a",
            boxShadow: "-8px 0 30px rgba(15, 23, 42, 0.16)",
          }}
        >
          <header style={{
            display: "flex", alignItems: "flex-start", justifyContent: "space-between",
            padding: 20, background: "linear-gradient(135deg, #fffbeb, #fff7ed)",
            borderBottom: "1px solid #fde68a",
          }}>
            <div>
              <h2 style={{ display: "flex", alignItems: "center", gap: 9, margin: 0, fontSize: 18 }}>
                <FaUserSlash style={{ color: "#d97706" }} /> Obemannade pass
              </h2>
              <p style={{ margin: "5px 0 0", color: "#64748b", fontSize: 12 }}>
                {unstaffedShifts.length} pass behöver bemannas
              </p>
            </div>
            <button type="button" onClick={() => setShowUnstaffedPanel(false)} aria-label="Stäng" style={{
              width: 32, height: 32, border: 0, borderRadius: 8, display: "grid",
              placeItems: "center", background: "#ffffff", color: "#475569",
              cursor: "pointer", boxShadow: "0 1px 3px rgba(15, 23, 42, 0.1)",
            }}><FaTimes /></button>
          </header>

          <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
            {unstaffedShifts.length === 0 ? (
              <div style={{ padding: "48px 16px", textAlign: "center", color: "#64748b" }}>
                <FaUserSlash style={{ marginBottom: 12, color: "#f59e0b", fontSize: 36 }} />
                <strong style={{ display: "block", marginBottom: 5, color: "#334155" }}>Inga obemannade pass</strong>
                <span style={{ fontSize: 13 }}>Alla pass i det valda schemat är bemannade.</span>
              </div>
            ) : unstaffedShifts.map((shift) => {
              const shiftDate = safeParseDate(shift.date);
              const isLocked = isShiftPayrollLocked(shift);
              const isBlocked = isShiftInteractionBlocked(shift);
              const dateLabel = shiftDate
                ? shiftDate.toLocaleDateString("sv-SE", {
                    weekday: "long", day: "numeric", month: "long", year: "numeric",
                  })
                : "Datum saknas";

              return (
                <button key={shift.originalId || shift.id} type="button" disabled={isBlocked} title={isLocked ? "Låst: löneunderlaget är godkänt" : undefined} onClick={() => {
                  setShowUnstaffedPanel(false);
                  handleEditShiftClick(shift);
                }} style={{
                  width: "100%", marginBottom: 10, padding: 14,
                  border: "1px solid #fde68a", borderLeft: "4px solid #f59e0b",
                  borderRadius: 10, background: "#fffbeb", color: "#0f172a",
                  cursor: isBlocked ? "not-allowed" : "pointer", textAlign: "left",
                }}>
                  {isLocked && <FaLock aria-label="Låst pass" style={{ float: "right", color: "#475569" }} />}
                  <strong style={{ display: "block", textTransform: "capitalize", fontSize: 14 }}>{dateLabel}</strong>
                  <span style={{ display: "block", marginTop: 6, color: "#92400e", fontSize: 13, fontWeight: 700 }}>
                    {shift.startTime && shift.endTime ? `${shift.startTime}–${shift.endTime}` : shift.time || "Tid saknas"}
                  </span>
                  <span style={{ display: "block", marginTop: 4, color: "#64748b", fontSize: 12 }}>
                    {shift.project || "Projekt saknas"}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>
      )}

      {showSchemaRequiredPopup && (
        <div
          onClick={() => setShowSchemaRequiredPopup(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17, 24, 39, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2100,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              background: "#ffffff",
              borderRadius: 16,
              boxShadow: "0 12px 30px rgba(0,0,0,0.16)",
              padding: 24,
              boxSizing: "border-box",
            }}
          >
            <h3
              style={{
                margin: "0 0 10px",
                fontSize: 20,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Schema saknas
            </h3>
 
            <p
              style={{
                margin: "0 0 20px",
                fontSize: 14,
                color: "#4b5563",
                lineHeight: 1.5,
              }}
            >
              Du måste skapa ett schemaläggning först.
            </p>
 
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                onClick={() => setShowSchemaRequiredPopup(false)}
                style={{
                  height: 40,
                  padding: "0 16px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  background: "#ffffff",
                  color: "#6b7280",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Stäng
              </button>
 
              <button
                onClick={() => {
                  setShowSchemaRequiredPopup(false);
                  setShowSchedulingPopup(true);
                }}
                style={{
                  height: 40,
                  padding: "0 16px",
                  borderRadius: 8,
                  border: "1px solid #2563eb",
                  background: "#3b82f6",
                  color: "#ffffff",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Skapa schema
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles live with this component; keep selectors and responsive rules together.
registerStyles("ShiftPage", `
.shift-day-scroll { min-width: 0; width: 100%; padding-bottom: 12px; }
.shift-day-columns { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 8px; }
.shift-day-column { min-width: 0; box-sizing: border-box; min-height: 500px; padding: 14px 8px; border: 1px solid #e2e8f0; border-radius: 16px; background: #f7f8fa; }
.shift-day-column.is-today { background: #eef8ff; }
.shift-day-column.is-month-start { border-left-color: #2563eb; box-shadow: inset 2px 0 0 #2563eb; }
.shift-day-column.is-month-start .shift-day-date { color: #2563eb; font-weight: 600; }
.shift-day-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; margin-bottom: 22px; }
.shift-day-header h3 { margin: 0 0 12px; color: #111827; font-size: clamp(13px, 1.3vw, 20px); overflow-wrap: anywhere; text-transform: capitalize; }
.shift-day-column.is-today h3 { color: #2563eb; }
.shift-day-date { display: flex; flex-wrap: wrap; gap: 6px; color: #7b879e; font-size: 14px; }
.shift-day-date strong { color: #111827; }
.shift-day-add { display: grid; place-items: center; flex: none; width: 36px; height: 36px; padding: 0; border: 0; border-radius: 50%; background: #2563eb; color: white; cursor: pointer; }
.shift-day-cards { display: flex; flex-direction: column; gap: 12px; }
.shift-day-card { position: relative; padding: 12px 10px; border-left: 4px solid #3b82f6; border-radius: 12px; background: white; box-shadow: 0 4px 12px rgba(15,23,42,.03); }
.shift-day-card.is-unpublished { background: #f3f4f6; }
.shift-day-card.is-unstaffed { border-color: #f59e0b; background: #fffbeb; }
.shift-day-card.is-locked { border-color: #94a3b8; background: #e8ebf0; }
.shift-day-card.is-deleting { border-color: #dc2626; background: #fee2e2; }
.shift-day-card-open { display: block; width: 100%; padding: 0; border: 0; background: transparent; text-align: left; color: #111827; cursor: pointer; font: inherit; }
.shift-day-card-open:disabled { cursor: not-allowed; color: #64748b; }
.shift-day-card-open:focus-visible, .shift-day-add:focus-visible { outline: 3px solid #2563eb; outline-offset: 4px; }
.shift-day-time { display: block; padding-right: 15px; font-size: clamp(12px, 1.1vw, 17px); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.shift-day-more { position: absolute; right: 10px; top: 15px; color: #64748b; font-size: 12px; }
.shift-day-person { display: flex; flex-wrap: nowrap; align-items: center; gap: 8px; margin-top: 14px; }
.shift-day-avatar { display: grid; place-items: center; flex: none; width: 36px; height: 36px; border-radius: 50%; background: #929bab; color: white; font-size: 14px; font-weight: 600; }
.shift-day-person-details { display: flex; flex: 1; flex-direction: column; gap: 6px; min-width: 0; font-size: 13px; }
.shift-day-person-details strong { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.shift-day-project { display: flex; align-items: center; gap: 6px; min-width: 0; color: #7b879e; }
.shift-day-project > span { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.shift-day-project i { flex: none; width: 8px; height: 8px; border-radius: 50%; background: #3b82f6; }
.shift-day-empty { text-align: center; color: #94a3b8; font-size: 13px; }
@media (max-width: 600px) { .shift-day-scroll { overflow-x: auto; } .shift-day-columns { grid-template-columns: repeat(7, minmax(190px, 1fr)); gap: 10px; } }

.shift-unstaffed-row {
  display: grid;
  grid-template-rows: 1fr;
  opacity: 1;
  visibility: visible;
  transition: grid-template-rows 320ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 220ms ease, visibility 0s;
}

.shift-unstaffed-row[data-expanded="false"] {
  grid-template-rows: 0fr;
  opacity: 0;
  visibility: hidden;
  transition: grid-template-rows 320ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 220ms ease, visibility 0s 320ms;
}

.shift-unstaffed-row-clip {
  min-height: 0;
  overflow: hidden;
}

.shift-unstaffed-chevron {
  transition: transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.shift-unstaffed-toggle:focus-visible {
  outline: 3px solid #f59e0b;
  outline-offset: 3px;
}

.shift-unstaffed-info-button {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-left: auto;
  padding: 0;
  border: 1px solid #d6a449;
  border-radius: 50%;
  background: #fff7db;
  color: #92400e;
  font-size: 12px;
  font-weight: 800;
  font-style: normal;
  cursor: pointer;
}

.shift-unstaffed-info-button:focus-visible {
  outline: 2px solid #92400e;
  outline-offset: 2px;
}

.shift-unstaffed-info-bubble {
  position: fixed;
  z-index: 2000;
  box-sizing: border-box;
  padding: 11px 13px;
  border: 1px solid #e8d6a7;
  border-radius: 8px;
  background: #fffdf5;
  color: #4b3a21;
  box-shadow: 0 10px 25px rgba(15, 23, 42, .18);
  font-size: 12px;
  line-height: 1.45;
  text-align: left;
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .shift-unstaffed-row,
  .shift-unstaffed-row[data-expanded="false"],
  .shift-unstaffed-chevron {
    transition: none;
  }
}
.shift-payroll-lock {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.shift-payroll-lock-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #475569;
  cursor: pointer;
}

.shift-payroll-lock-button:hover,
.shift-payroll-lock-button:focus-visible {
  background: #e2e8f0;
  outline: 2px solid #94a3b8;
}

.shift-payroll-lock-bubble {
  position: fixed;
  z-index: 2000;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  box-shadow: 0 10px 25px rgba(15, 23, 42, .18);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
  text-align: left;
  white-space: normal;
  pointer-events: auto;
}

.shift-row-stats,
.shift-row-stats > div {
  min-width: 0;
}

.shift-row-stats > div > span,
.shift-row-stats > div > strong,
.shift-row-stats > div > div,
.shift-row-stats small {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
`);
